import type { ServiceAreaFaq, ServiceAreaSlug } from "@/config/service-areas";
import type { ServicePage } from "@/config/services";

type ServiceSlug =
  | "roof-replacement"
  | "roof-repair"
  | "asphalt-shingle-roofing"
  | "metal-roofing"
  | "flat-roofing"
  | "roof-inspection-maintenance";

type ServiceContent = Partial<Omit<ServicePage, "slug" | "image">>;
type ServiceAreaContent = {
  intro: string;
  localContext: string;
  assessmentFocus: readonly string[];
  propertyContexts: readonly string[];
  faqs: readonly ServiceAreaFaq[];
};

export const services = {
  "roof-replacement": {
    title: "아스팔트 싱글 지붕 교체",
    shortTitle: "아스팔트 교체",
    eyebrow: "급경사 재지붕",
    description:
      "메트로 밴쿠버 급경사 주택을 위한 아스팔트 싱글 전체 철거, underlayment, 싱글 설치, 플래싱 통합 및 폐기물 처리.",
    definition:
      "아스팔트 싱글 교체는 기존 싱글 덮개를 제거하고, 기판 및 underlayment 요구 사항을 처리한 뒤 새 싱글 시스템을 설치하고 플래싱과 관통부를 다시 통합하는 급경사 전체 재지붕입니다.",
    metaTitle: "아스팔트 싱글 지붕 교체 밴쿠버 BC | Revolin Roofing",
    metaDescription:
      "메트로 밴쿠버 주택 건축용 아스팔트 싱글 교체. 철거, underlayment, 플래싱 통합 및 평가 후 서면 범위.",
    forWhom:
      "싱글이 광범위하게 마모되었거나 반복 수리가 있거나, 주택의 잔여 계획과 맞지 않거나, 개조로 전체 재지붕이 더 일관된 범위인 소유자.",
    warningSigns: [
      "컬링, 균열 또는 광범위한 과립 손실",
      "여러 면에서 반복 수리 후에도 계속 실패",
      "다시 밀착되지 않는 부서지기 쉬운 싱글",
      "노후화된 계곡, ridge 및 처마",
      "전체 재지붕이 더 합리적인 개조 작업",
    ],
    evaluates: [
      "기존 층 수 및 데크 상태",
      "경사, 계곡, dormer 및 기하학",
      "환기 및 다락방 습기 징후",
      "플래싱 및 관통부 목록",
      "접근, 보호 및 폐기",
    ],
    causes: [
      "정상적인 수명 말기 풍화",
      "만성 이끼 및 습기",
      "누적된 풍손",
      "환기 부족으로 가속된 노화",
      "세부 사항이 약한 이전 설치",
    ],
    methodology: [
      "상태와 옵션 문서화",
      "철거, 보호 및 폐기 계획",
      "발견된 데크 수리",
      "underlayment, 플래싱, 필드 싱글을 순서대로 설치",
      "ridge, 계곡, 가장자리 및 관통부를 완전한 어셈블리로 마감",
    ],
    materials: [
      "건축용 아스팔트 싱글",
      "underlayment 및 필요 시 방수막",
      "계곡, 벽 및 관통부 플래싱",
      "ridge 및 환기 부속품",
      "패스너 및 가장자리 부품",
    ],
    bcClimate:
      "해안 비와 이끼 성장은 로어 메인랜드 싱글 지붕에서 underlayment, 계곡 세부 사항 및 환기를 특히 중요하게 만듭니다.",
    whenAppropriate:
      "싱글 열화가 시스템 전반이거나, 패치를 계속하면 다른 단기 실패가 해결되지 않을 때.",
    whenOtherService: "국소 풍손은 필드가 여전히 양호하면 spot 수리가 적합할 수 있습니다.",
    solutionCategories: [
      {
        title: "건축용 라미네이트 시스템",
        body: "기하학, 노출 및 설계 의도에 맞게 선택한 입체형 주거용 싱글.",
      },
      {
        title: "underlayment 및 배수층",
        body: "필드 싱글과 계곡, 처마 및 전환을 조정합니다.",
      },
      {
        title: "플래싱 재통합",
        body: "벽, 굴뚝, 통풍구 및 가장자리를 새 어셈블리의 일부로 세부 설계합니다.",
      },
    ],
    repairVsReplace: {
      repair: "손상이 국소적이고 호환 싱글이 깨끗하게 통합될 때 spot 수리가 실용적일 수 있습니다.",
      replace:
        "마모가 광범위하거나 어셈블리가 내구성 있는 패치를 받을 수 없을 때 교체가 더 적절합니다.",
    },
    scopeFactors: [
      "지붕 면적 및 경사",
      "층 수",
      "데크 수리",
      "계곡 및 dormer 복잡성",
      "자재 선택",
      "접근 및 폐기",
    ],
    process: [
      "덮개, 데크 징후 및 환기 평가",
      "자재 및 세부 사항 옵션이 포함된 서면 범위",
      "부동산 보호 및 폐기와 함께 철거",
      "데크 수리, underlayment, 플래싱 및 싱글을 순서대로 설치",
      "최종 점검 및 완료 검토",
    ],
    publicPriceHint:
      "지붕 면적, 경사, 층 수, 데크 수리, 계곡·dormer 복잡성 및 자재 선택이 프로젝트 범위에 영향을 줍니다. 평가를 요청하여 프로젝트별 범위를 확인하세요.",
    faqs: [
      {
        question: "아스팔트 싱글 교체란 무엇입니까?",
        answer:
          "기존 싱글을 제거하고 기판·underlayment를 처리한 뒤 새 싱글 시스템을 설치하고 플래싱과 관통부를 다시 통합하는 급경사 전체 재지붕입니다.",
      },
      {
        question: "아스팔트 싱글 교체는 얼마나 걸립니까?",
        answer:
          "대부분의 단독주택 재지붕은 주 단위가 아니라 일 단위로 측정되지만, 기간은 크기, 경사, 날씨 창 및 철거 중 발견되는 데크 수리에 따라 달라집니다. 서면 범위가 작업 전 기대를 설정합니다.",
      },
      {
        question: "기존 싱글 위에 새 싱글을 설치할 수 있습니까?",
        answer:
          "overlay가 기술적으로 가능한 경우도 있지만, 철거가 일반적으로 선호됩니다. 데크 검사, 올바른 underlayment 및 플래싱 통합이 가능하고, 무게를 쌓거나 문제를 새 덮개 아래에 가두지 않습니다.",
      },
      {
        question: "철거 중 rotten 데크가 발견되면 어떻게 됩니까?",
        answer:
          "발견된 데크 손상은 문서화되고 새 시스템 전에 수리됩니다. 명확한 범위가 데크 작업 측정·범위를 미리 설명하여 중간 발견이 놀라움이 아닌 투명한 처리가 됩니다.",
      },
      {
        question: "메트로 밴쿠버 날씨에 어떤 싱글이 가장 오래 갑니까?",
        answer:
          "건축용 라미네이트 싱글이 이 지역 주거용 일반 선택입니다. 이 기후에서는 underlayment 품질, 계곡·가장자리 세부 사항 및 다락방 환기가 싱글 제품 자체만큼 실제 수명에 영향을 줍니다.",
      },
    ],
  },
  "roof-repair": {
    title: "긴급 지붕 누수 수리",
    shortTitle: "긴급 누수 수리",
    eyebrow: "활성 침수",
    description:
      "일정이 허용할 때 당일 우선 누수 진단과, 지붕 어셈블리를 통해 물이 들어오는 메트로 밴쿠버 주택의 임시 또는 영구 국소 수리.",
    definition:
      "긴급 지붕 누수 수리는 덮개, 플래싱, 관통부 또는 관련 인터페이스를 통한 활성 또는 급속한 침수의 집중 조사 및 안정화입니다. 조건이 허용하면 임시 dry-in 및 영구 국소 수리가 포함될 수 있습니다.",
    metaTitle: "긴급 지붕 누수 수리 메트로 밴쿠버 | Revolin Roofing",
    metaDescription:
      "메트로 밴쿠버 주택 누수 진단 및 국소 긴급 수리. 일정 허용 시 당일 방문. 원인 우선 평가와 명확한 다음 단계.",
    forWhom:
      "활성 물방울, 비 중 급작스러운 천장 얼룩, 폭풍 후 다락방 습기, 또는 더 큰 범위 계획 전 안전한 진단과 격리가 필요한 손상이 있는 소유자.",
    warningSigns: [
      "비 중 또는 후 활성 물방울 또는 지속적 습기",
      "새로 생기거나 확대되는 천장·벽·조명 얼룩",
      "젖은 단열재, sheathing 또는 다락방 골조",
      "계곡, 굴뚝, skylight 또는 배관 통풍구를 따라 물 이동",
      "날씨 사건 후 진행되는 내부 손상",
    ],
    evaluates: [
      "내부 증거 및 예상 수로",
      "영향 구역 주변 덮개 상태",
      "플래싱, 관통부, 계곡 및 벽 인터페이스",
      "임시 보호 대 영구 수리 필요성",
      "인접 자재가 내구성 있는 작업을 지원할 수 있는지",
    ],
    causes: [
      "실패한 플래싱 또는 pipe boot 실란트",
      "풍에 들리거나 누락된 덮개",
      "잔해로 막힌 배수가 물을 집중",
      "폭풍으로 손상된 ridge, 가장자리 또는 전환",
      "진짜 원인을 해결하지 않은 이전 패치",
    ],
    methodology: [
      "내부·외부 증거를 안전하게 문서화",
      "얼룩 위치만이 아니라 가장 가능성 높은 진입 경로 추적",
      "날씨·접근이 필요하면 임시 dry-in으로 안정화",
      "주변 어셈블리가 서비스 가능할 때 호환 국소 수리 실행",
      "잔여 위험 및 추가 작업 필요 여부 설명",
    ],
    materials: [
      "기존 시스템과 호환되는 덮개 자재",
      "적절한 경우 underlayment 또는 막 패치",
      "플래싱 금속 및 관통부 boot",
      "올바른 세부 사항의 일부로만 사용하는 실란트",
      "필요 시 임시 방수포 또는 dry-in 자재",
    ],
    bcClimate:
      "메트로 밴쿠버의 잦은 비는 작은 결함도 반복적인 내부 손상을 일으킬 수 있습니다. 진짜 경로를 남긴 표면 패치보다 시기적절한 진단이 더 중요합니다.",
    whenAppropriate:
      "침수가 국소적이고 주변 지붕이 서비스 가능하며, 안전하게 내구성 있는 수리 또는 임시 dry-in이 가능할 때.",
    whenOtherService:
      "광범위한 덮개 실패, 여러 독립 누수 또는 수명 말기 어셈블리는 더 넓은 수리 계획, 폭풍 dry-in만, 또는 전체 교체 논의가 필요할 수 있습니다.",
    solutionCategories: [
      {
        title: "원인 진단",
        body: "내부 증거를 덮개 아래 측면 이동을 포함하여 물이 들어올 가능성이 가장 높은 외부 세부 사항과 연결합니다.",
      },
      {
        title: "임시 dry-in",
        body: "폭풍 손상 후 또는 같은 방문에서 영구 자재를 완료할 수 없을 때 개구부를 안정화합니다.",
      },
      {
        title: "국소 영구 수리",
        body: "주변 지붕 시스템과 호환되는 자재로 실패 부품을 교체하거나 재세부 설계합니다.",
      },
    ],
    repairVsReplace: {
      repair:
        "하나의 실패 구역이 식별되고 주변 자재가 호환 작업을 받을 만큼 양호할 때 집중 수리가 일반적으로 적절합니다.",
      replace:
        "조사 결과 광범위한 열화, 여러 독립 실패 또는 내구성 있는 패치를 지원할 수 없는 어셈블리가 나타나면 교체 논의가 시작됩니다.",
    },
    scopeFactors: [
      "접근 및 지붕 높이",
      "날씨 창",
      "숨겨진 손상 범위",
      "자재 호환성",
      "관통부 수",
      "임시 보호 필요성",
    ],
    process: [
      "보이는 상황의 세부 사항과 사진을 전화 또는 전송",
      "분류 및 일정: 가능 시 당일 방문",
      "내부·외부 문서화와 함께 지붕 위 원인 진단",
      "조건이 허용하면 임시 dry-in 또는 영구 국소 수리",
      "조사 결과, 완료 작업 및 권장 다음 단계의 서면 요약",
    ],
    publicPriceHint:
      "긴급 범위는 접근, 작업 중 날씨, 물이 이동한 거리 및 영구 수리 전 임시 보호 필요 여부에 따라 달라집니다. 평가를 요청하여 프로젝트별 범위를 확인하세요.",
    faqs: [
      {
        question: "긴급 지붕 누수 수리란 무엇입니까?",
        answer:
          "덮개, 플래싱, 관통부 또는 관련 인터페이스를 통한 활성 또는 급속한 침수의 집중 조사 및 안정화입니다. 조건이 허용하면 임시 dry-in 및 영구 국소 수리가 포함될 수 있습니다.",
      },
      {
        question: "비가 오는 중에도 누수를 고칠 수 있습니까?",
        answer:
          "활성 비 중 안전한 첫 단계는 보통 임시 dry-in입니다. 대부분의 영구 지붕 자재는 건조 조건이 필요하므로, 영구 수리는 적절한 날씨 창에 완료됩니다.",
      },
      {
        question: "천장 얼룩이 눈에 보이는 지붕 손상과 멀리 떨어져 있는 이유는?",
        answer:
          "물은 한 세부 사항에서 들어와 underlayment, 골조 또는 sheathing을 따라 이동한 뒤 실내에 나타납니다. 진단은 얼룩 위가 아니라 예상 진입 경로를 추적합니다.",
      },
      {
        question: "메트로 밴쿠버에서 당일 누수 수리를 제공합니까?",
        answer:
          "Revolin은 일정과 일광이 허용할 때 활성 누수에 당일 방문을 목표하지만, 방문 시간은 보장되지 않습니다. 사진과 명확한 설명을 공유하면 분류에 도움이 됩니다.",
      },
      {
        question: "누수 수리가 임시인지 영구인지 어떻게 알 수 있습니까?",
        answer:
          "날씨, 자재 가용성 및 조사 결과에 따라 달라집니다. 첫 방문에서 안전하게 내구성 있는 국소 수리가 가능하면 그렇게 하고, 그렇지 않으면 임시 dry-in 후 영구 작업 범위를 서면으로 정합니다.",
      },
      {
        question: "긴급 지붕 누수 수리 비용은 얼마입니까?",
        answer:
          "범위는 지붕 높이·접근, 숨겨진 손상 범위 및 수리에 추가로 임시 보호가 필요한지에 따라 달라집니다. Revolin은 평가 후 프로젝트별 범위를 제공합니다.",
      },
    ],
  },
  "asphalt-shingle-roofing": {
    title: "건축용 아스팔트 지붕널 루핑",
    shortTitle: "아스팔트 지붕널",
    eyebrow: "급경사 시스템",
    description:
      "적합한 급경사 지붕 시스템을 갖춘 많은 메트로 밴쿠버 주택을 위한 실용적이고 디자인을 고려한 지붕 카테고리입니다.",
    forWhom:
      "건축용 아스팔트 슁글은 주택 소유자가 친숙한 시스템, 광범위한 미적 선택 및 간단한 향후 서비스 가능성을 원하는 많은 경사진 주거용 지붕에 적합합니다.",
    warningSigns: [
      "과립 손실 또는 노출된 매트",
      "컬링, 균열 또는 옮겨진 대상 포진",
      "계곡, 가장자리 또는 관통부 주변의 손상",
      "지붕 표면을 가리는 이끼 성장",
      "여러 평면에 걸쳐 반복적으로 수리",
    ],
    evaluates: [
      "지붕 경사 및 기하학",
      "적절한 조사 후 데크 상태",
      "밑 깔개, 처마 및 계곡 접근",
      "환기 및 흡배기 경로",
      "깜박이는 인터페이스 및 배수",
    ],
    solutionCategories: [
      {
        title: "건축용 라미네이트 지붕널",
        body: "주거용 급경사 지붕에 널리 사용되는 치수 프로파일이며 조립 및 설계 요구 사항에 따라 선택됩니다.",
      },
      {
        title: "Underlayment 및 물 흘리기 세부 사항",
        body: "눈에 보이는 지붕널은 랩, 밸리, 가장자리 및 전환을 포함하는 조정된 어셈블리의 한 레이어입니다.",
      },
      {
        title: "플래싱 통합",
        body: "벽, 굴뚝, 배관 통풍구 및 지붕 기능의 인터페이스에는 지붕 널 필드와의 의도적인 순서가 필요합니다.",
      },
    ],
    repairVsReplace: {
      repair:
        "일치하는 경우 포함된 수리가 실용적일 수 있으며, 호환 가능한 대상 포진은 깨지기 쉬운 주변 필드를 손상시키지 않고 통합될 수 있습니다.",
      replace:
        "지붕널이 여러 지붕 면에 걸쳐 광범위하게 부서지기 쉽거나 마모되거나 영향을 받은 경우 교체가 더 적절할 수 있습니다.",
    },
    scopeFactors: [
      "지붕 피치 및 전체 면적",
      "계곡, 지붕창 및 관통부 수",
      "기존 레이어 및 데크 수리",
      "슁글 프로파일 및 액세서리 선택",
      "환기 및 후레싱 작업",
      "액세스 및 사이트 보호",
    ],
    process: [
      "경사·기하·노출 평가",
      "조립 요구 사항에 맞는 제품 옵션 검토",
      "수리·교체 경로를 포함한 서면 권장",
      "국소 수리 또는 재지붕 계획 시 다음 단계 정리",
      "선택한 시스템에 맞는 세부 사항 논의",
    ],
    publicPriceHint:
      "범위는 면적, 경사, 계곡·관통부 복잡성 및 자재 선택에 따라 달라집니다. 평가를 요청하여 프로젝트별 범위를 확인하세요.",
    faqs: [
      {
        question: "모든 경사진 지붕에 건축용 지붕널이 적합합니까?",
        answer:
          "아니요. 경사, 기판, 노출, 형상 및 현지 요구 사항을 고려해야 합니다. 제품을 선택하기 전에 지붕은 조립품으로 평가됩니다.",
      },
      {
        question: "색상이 건축 결과에 영향을 미치나요?",
        answer:
          "예. 지붕 색상은 클래딩, 트림, 매스 및 거리에서의 가시성과 상호 작용합니다. 샘플은 화면뿐만 아니라 실제 건물과 비교하여 고려되어야 합니다.",
      },
    ],
  },
  "metal-roofing": {
    title: "금속 지붕 교체",
    shortTitle: "금속 교체",
    eyebrow: "프리미엄 금속 재지붕",
    description: "적합한 급경사 건축을 위한 standing seam 또는 다른 프리미엄 금속 재지붕.",
    definition:
      "금속 지붕 교체는 기하학, 기판, underlayment 및 건축 의도를 평가한 뒤 standing seam 등 새 금속 지붕 시스템을 설치합니다.",
    metaTitle: "금속 지붕 교체 메트로 밴쿠버 | Revolin Roofing",
    metaDescription:
      "메트로 밴쿠버 주택 standing seam 및 프리미엄 금속 지붕 교체. 기하학 중심 시스템 선택과 완전한 플래싱 통합.",
    forWhom: "적합한 기하학, 건축 및 장기 계획에 맞게 프리미엄 금속 시스템을 선택하는 소유자.",
    warningSigns: [
      "수명 말기 덮개와 금속을 새 시스템으로 선택",
      "standing seam에 적합한 건축",
      "프리미엄 덮개 프로필을 원함",
      "금속 패널로 표현하기 좋은 복잡한 기하학",
    ],
    evaluates: [
      "프로필 선택",
      "underlayment 및 기판",
      "결로 제어 접근",
      "플래싱 및 관통부",
      "음향 및 설계 기대",
    ],
    causes: [
      "계획된 시스템 업그레이드",
      "기존 지붕 수명 말기",
      "설계 개조",
      "금속 어셈블리에 맞는 성능 목표",
    ],
    methodology: [
      "기하학 적합성 확인",
      "패널 시스템 및 underlayment 명세",
      "기판 준비",
      "패널 및 플래싱을 완전한 시스템으로 설치",
      "ridge, 가장자리 및 관통부를 의도적으로 마감",
    ],
    materials: [
      "standing seam 또는 지정 금속 패널",
      "underlayment 시스템",
      "시스템에 필요한 클립·패스너",
      "플래싱 및 closure",
    ],
    bcClimate:
      "서해안 건축은 standing seam 금속과 잘 맞지만, underlayment, 환기 및 세부 사항이 습한 날씨에서 실제 성능을 결정합니다.",
    whenAppropriate: "금속이 선택된 시스템이고 프로젝트 범위가 완전한 재지붕일 때.",
    whenOtherService: "기존 금속 지붕이 여전히 양호하면 부품 수리가 적합할 수 있습니다.",
    solutionCategories: [
      { title: "standing seam 시스템", body: "지정 시 숨겨진 고정을 갖춘 프리미엄 패널 미학." },
      { title: "underlayment 및 기판 준비", body: "금속 아래 배수 기반을 구축합니다." },
      {
        title: "건축 세부 사항",
        body: "가장자리, 계곡 및 벽을 성능과 외관 모두를 위해 마감합니다.",
      },
    ],
    repairVsReplace: {
      repair: "패널이 여전히 양호할 때 기존 금속 수리.",
      replace: "새 금속 시스템이 목표이거나 기존 덮개가 끝났을 때 교체.",
    },
    scopeFactors: [
      "지붕 면적 및 기하학",
      "패널 프로필",
      "underlayment 선택",
      "접근",
      "관통부 복잡성",
    ],
    process: [
      "기하학 및 시스템 적합성 확인",
      "패널 프로필, underlayment 및 세부 사항 명세",
      "설계 결정이 문서화된 서면 범위",
      "기판 준비 및 패널 설치",
      "세부 사항 검토 및 완료 점검",
    ],
    publicPriceHint:
      "금속 재지붕 범위는 지붕 기하학, 패널 프로필, underlayment 전략 및 관통부 복잡성에 따라 달라집니다. 평가를 요청하여 서면 범위를 확인하세요.",
    faqs: [
      {
        question: "금속 지붕 교체란 무엇입니까?",
        answer:
          "기하학, 기판, underlayment 및 건축 의도를 평가한 뒤 새 금속 지붕 시스템 - often standing seam - 을 설치합니다.",
      },
      {
        question: "금속 지붕은 비 소리가 시끄럽습니까?",
        answer:
          "단단한 데크 위 underlayment와 단열이 있는 주거 재지붕에서는 대부분 기대보다 훨씬 조용합니다. 개방 골조 위 패널에서 오는 큰 소리 연상이 있습니다. 음향 기대는 시스템 선택 중 논의합니다.",
      },
      {
        question: "standing seam과 노출 패스너 금속의 차이는?",
        answer:
          "standing seam은 raised seam 아래 클립에 고정을 숨기고 패널 이동을 허용합니다. 노출 패스너 패널은 비용이 낮지만 장기 패스너 유지가 필요합니다. 프리미엄 주거용은 보통 standing seam을 권장합니다.",
      },
      {
        question: "기존 싱글 위에 금속 지붕을 설치할 수 있습니까?",
        answer:
          "구조적으로 가능한 경우도 있지만, 데크 검사와 underlayment·플래싱 시스템을 올바르게 구축하기 위해 철거가 일반적으로 선호됩니다.",
      },
      {
        question: "복잡한 지붕 형태에도 금속이 작동합니까?",
        answer:
          "가능하지만 hip, 계곡, dormer 및 dead-end 세부 사항에서 숙련된 금속 작업이 필요하고 비용이 올라갑니다. 기하학 검토가 첫 단계입니다.",
      },
    ],
  },
  "flat-roofing": {
    title: "평지·저경사 지붕 교체: SBS / TPO / PVC",
    shortTitle: "평지 교체",
    eyebrow: "저경사 재지붕",
    description: "SBS, TPO, PVC 또는 다른 지정 막 시스템을 사용한 주거·상업 저경사 전체 재지붕.",
    definition:
      "평지 지붕 교체는 저경사 어셈블리를 제거하거나 recover하고, 배수, 기판 및 프로젝트 요구에 맞게 선택한 새 막 시스템을 설치합니다.",
    metaTitle: "SBS·TPO·PVC 평지 지붕 교체 BC | Revolin Roofing",
    metaDescription:
      "메트로 밴쿠버 평지·저경사 지붕 SBS, TPO, PVC 또는 지정 막 시스템 전체 교체. 평가 후 범위.",
    forWhom:
      "막 반복 실패, 젖은 단열, obsolete 시스템 또는 저경사 지붕의 연계 외부 개조가 있는 소유자.",
    warningSigns: [
      "여러 막 실패",
      "젖은 단열재",
      "obsolete 또는 서비스 불가 시스템",
      "연계 외부 개조",
      "시스템 전반 마모와 함께 만성 침수",
    ],
    evaluates: [
      "막 유형 옵션",
      "단열 및 기판 필요",
      "배수구 및 scupper",
      "parapet 및 관통부",
      "접근 및 보호",
    ],
    causes: [
      "막 수명 말기",
      "단열 포화",
      "배수 설계 한계",
      "UV 및 열 노화",
      "누적된 세부 실패와 높은 관통부 밀도",
    ],
    methodology: [
      "조사가 필요한 경우 습기 포함 어셈블리 평가",
      "프로젝트에 맞는 막 전략 선택",
      "명세에 따라 제거 또는 recover",
      "단열 및 막 층을 순서대로 설치",
      "배수구, parapet 및 관통부를 시스템으로 마감",
    ],
    materials: [
      "SBS, TPO, PVC 또는 다른 지정 막",
      "범위에 따른 단열 및 cover board",
      "배수 및 플래싱 부속품",
      "시스템에 맞는 접착제, 패스너 또는 열 공정",
    ],
    bcClimate:
      "비가 많은 메트로 밴쿠버 저경사 지붕은 마케팅 라벨보다 배수 설계와 견고한 막 세부 사항에 더 의존합니다.",
    whenAppropriate: "저경사 어셈블리가 소진되었거나 더 넓은 프로젝트의 일부로 갱신될 때.",
    whenOtherService: "여전히 양호한 시스템에서는 국소 막 수리가 가능할 수 있습니다.",
    solutionCategories: [
      {
        title: "막 시스템 선택",
        body: "프로젝트 조건에 맞게 SBS, TPO, PVC 또는 다른 지정 어셈블리를 선택합니다.",
      },
      { title: "어셈블리 교체", body: "승인된 범위에 따라 막 및 관련 층을 처리합니다." },
      {
        title: "배수 및 세부 사항",
        body: "배수구, parapet 및 관통부를 핵심 방수 작업으로 처리합니다.",
      },
    ],
    repairVsReplace: {
      repair: "막 필드가 여전히 viable하면 수리.",
      replace: "시스템이 소진되면 저경사 전체 교체.",
    },
    scopeFactors: ["면적 및 기하학", "막 유형", "단열 필요", "관통부 밀도", "접근 및 폐기"],
    process: [
      "필요 시 습기 조사를 포함한 어셈블리 평가",
      "막 시스템 선택 및 서면 범위",
      "명세에 따른 기존 어셈블리 제거 또는 recover",
      "단열 및 막을 순서대로 설치",
      "배수구, parapet 및 관통부 세부 사항 검토",
    ],
    publicPriceHint:
      "저경사 교체 범위는 면적, 막 선택, 단열 필요, 배수 작업 및 관통부 밀도에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "SBS·TPO·PVC 평지 지붕 교체란?",
        answer:
          "저경사 어셈블리를 제거하거나 recover하고 배수·기판·프로젝트 요구에 맞는 새 막 시스템을 설치합니다.",
      },
      {
        question: "SBS, TPO, PVC: 어떤 막이 최선입니까?",
        answer:
          "보편적으로 최선은 없습니다. 2겹 SBS는 지역에서 견고한 redundancy를 갖고, TPO와 PVC는 열 접착 단일겹 시스템입니다. 기판, 배수, 옥상 사용 및 예산이 결정합니다.",
      },
      {
        question: "철거 대 recover 중 어떤 것이 맞습니까?",
        answer:
          "기존 어셈블리가 건조하고 양호하면 recover가 비용·폐기를 줄일 수 있지만, 젖은 단열이나 여러 층은 철거가 정직한 경로입니다. 결정 전 습기 조사가 중요합니다.",
      },
      {
        question: "평지 교체는 얼마나 방해가 됩니까?",
        answer:
          "소음, 토치·접착 작업 냄새, 자재 적재 및 보호 동선이 예상됩니다. 입주는 보통 가능하고, 서면 범위가 순서·작업 시간·보호 조치를 미리 정합니다.",
      },
      {
        question: "교체 중 배수를 개선할 수 있습니까?",
        answer:
          "만성 침수를 바로잡을 적기입니다. tapered 단열, 배수구 추가·이전 및 scupper 작업을 새 어셈블리에 설계할 수 있습니다.",
      },
    ],
  },
  "roof-inspection-maintenance": {
    title: "지붕 건강 점검 + 유지보수",
    shortTitle: "건강 점검",
    eyebrow: "상태 우선",
    description: "검사, 사진, 소규모 실란트·유지보수 작업 및 계획을 위한 지붕 상태 보고.",
    definition:
      "지붕 건강 점검은 덮개, 플래싱, 배수 및 접근 가능한 관련 부품의 구조화된 시각 평가로, 일반적으로 사진 문서화와 사전 합의된 제한적 유지보수를 함께합니다.",
    metaTitle: "지붕 건강 점검·유지보수 밴쿠버 | Revolin Roofing",
    metaDescription:
      "메트로 밴쿠버 주택 지붕 건강 점검, 사진 및 유지보수 우선순위. 수리·교체 결정 전 명확한 상태 문서화.",
    forWhom: "매수·매도 준비, 개조 계획, 초기 경고 징후, 또는 우기 전 기록이 필요한 소유자.",
    warningSigns: [
      "지붕 연식·잔여 상태 불확실",
      "원인 불명의 작은 얼룩",
      "계절 잔해 및 이끼 축적",
      "관통부의 노후 실란트",
      "서면 계획 기준 필요",
    ],
    evaluates: [
      "면별 덮개 상태",
      "플래싱 및 관통부",
      "배수 및 홈통 인터페이스",
      "눈에 보이는 환기 부품",
      "유지보수 대 수리 항목 우선순위",
    ],
    causes: [
      "문서화 없는 정상 노화",
      "연기된 유지보수",
      "현장별 날씨 노출",
      "품질 불명의 이전 작업",
      "매수·개조 결정 필요",
    ],
    methodology: [
      "우려 사항 및 접근 가능한 이력 검토",
      "외부 지붕 구역을 체계적으로 검사",
      "대표 상태 사진",
      "합의된 소규모 유지보수만 완료",
      "명확한 상태 요약 및 옵션 전달",
    ],
    materials: [
      "사진 문서화",
      "범위에 포함된 소규모 실란트·패스너",
      "범위 시 제한적 잔해 청소",
      "서면 상태 메모",
    ],
    bcClimate:
      "계절 비는 가을 전·폭풍 후 점검이 배수 막힘, 들린 자재 및 초기 플래싱 실패를 잡는 데 유용합니다.",
    whenAppropriate: "수리, 교체 또는 추가 조사 전 명확성이 필요할 때.",
    whenOtherService: "활성 대형 누수나 폭풍 개구부는 긴급·수리 경로로.",
    solutionCategories: [
      { title: "문서화된 검사", body: "지붕 어셈블리에서 보이고 접근 가능한 것의 구조화된 검토." },
      { title: "소규모 유지보수", body: "범위에 포함되고 적절할 때만 제한적 실란트·잔해 작업." },
      { title: "계획 요약", body: "즉각 조치와 중기 모니터링 항목을 분리합니다." },
    ],
    repairVsReplace: {
      repair: "많은 발견은 전체 교체가 아니라 유지보수 또는 국소 수리입니다.",
      replace: "관찰된 상태가 더 넓은 경로를 지원할 때만 교체를 논의합니다.",
    },
    scopeFactors: [
      "지붕 크기·복잡성",
      "접근",
      "사진 문서화 깊이",
      "포함 유지보수",
      "부동산의 지붕 시스템 수",
    ],
    process: [
      "건강 점검 예약 및 우려 사항 설명",
      "사진 문서화와 체계적인 외부 검사",
      "방문 중 합의된 소규모 유지보수 완료",
      "우선순위가 정리된 서면 상태 요약",
      "권장 수리에 대한 후속 논의",
    ],
    publicPriceHint:
      "건강 점검 범위는 지붕 크기·복잡성, 접근, 문서화 및 포함 유지보수에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "지붕 건강 점검 + 유지보수란?",
        answer:
          "덮개, 플래싱, 배수 및 접근 가능한 부품의 구조화된 시각 평가와, 일반적으로 사진 및 사전 합의된 제한적 유지보수입니다.",
      },
      {
        question: "지붕은 얼마나 자주 검사해야 합니까?",
        answer:
          "가을 비 전 점검과 주요 강풍 후 점검이 현지에서 유용합니다. 수목이 많거나 예상 수명 후반의 지붕은 더 자주 봅니다.",
      },
      {
        question: "건강 점검 후 무엇을 받습니까?",
        answer:
          "덮개, 플래싱, 배수, 환기 상태의 사진 문서 요약: 즉시 수리, 모니터링, 정상 마모로 구분된 계획 문서입니다.",
      },
      {
        question: "매수·매도 전 점검이 가치가 있습니까?",
        answer:
          "지붕은 갱신 비용이 큰 부품 중 하나입니다. 문서화된 상태 기준이 협상 불확실성을 줄입니다. 이는 루핑 상태 검토이지 공식 pre-purchase 홈 검사가 아닙니다.",
      },
      {
        question: "검사 중 발견된 문제를 고칩니까?",
        answer:
          "관통부 소규모 실란트 등 합의된 유지보수는 방문 중 완료할 수 있습니다. 더 큰 발견은 문서화하고 별도 범위로 정해 현장 압박 없이 결정할 수 있게 합니다.",
      },
    ],
  },
  "moss-removal-roof-gutter": {
    title: "이끼 제거 + 지붕·홈통 패키지",
    shortTitle: "이끼·홈통 패키지",
    eyebrow: "유지보수 패키지",
    description:
      "유기물 성장과 막힌 배수가 지붕 마모를 가속하는 메트로 밴쿠버 주택을 위한 이끼 처리, 잔해 제거, 홈통 청소 및 시각적 지붕 검사.",
    definition:
      "이끼 제거와 지붕·홈통 유지보수를 결합한 서비스로, 지붕 표면 유기물, 홈통·downspout 청소 및 계획을 위한 가시 상태 문서화를 포함합니다.",
    metaTitle: "이끼 제거·홈통 청소 밴쿠버 | Revolin Roofing",
    metaDescription:
      "메트로 밴쿠버 주택 이끼 제거, 잔해 청소, 홈통 청소 및 시각 지붕 검사. 배수와 덮개 건강에 초점.",
    forWhom: "수목 아래 싱글·홈통에 이끼, 지의 또는 heavy 잔해가 있는 소유자.",
    warningSigns: [
      "싱글에 습기를 가두는 이끼 매트",
      "평상시 비에 홈통 넘침",
      "북향 경사의 유기물 성장",
      "계곡의 잔해 댐",
      "downspout 배수 불량",
    ],
    evaluates: [
      "이끼·유기물 범위",
      "성장 아래 덮개 상태",
      "홈통·downspout 유량",
      "계곡·가장자리 잔해",
      "수리가 이미 필요한 손상 여부",
    ],
    causes: [
      "그늘진 습한 해안 조건",
      "수목 잔해",
      "방치된 홈통 유지보수",
      "표면 질감이 유기물을 고정",
      "서늘한 달에 지붕면 건조 제한",
    ],
    methodology: [
      "조경·접근 보호",
      "덮개에 맞는 방법으로 이끼·잔해 제거",
      "홈통 청소 및 downspout flush",
      "시각적 상태 검토",
      "청소 중 발견된 수리 우선순위 기록",
    ],
    materials: [
      "지붕 유형에 맞는 부드러운 청소 도구",
      "사용 시 승인된 처리 제품",
      "홈통 scoop 및 flush 장비",
    ],
    bcClimate:
      "로어 메인랜드 그늘과 강우는 이끼에 이상적입니다. 성장은 덮개에 습기를 가두고 과립 손실을 가속할 수 있습니다.",
    whenAppropriate: "덮개가 근본적으로 온전하고 성장·잔해·배수가 주요 문제일 때.",
    whenOtherService: "누락 싱글, 활성 누수, 실패 플래싱은 청소만으로는 부족합니다.",
    solutionCategories: [
      { title: "이끼·유기물 제거", body: "적절한 지붕 표면에서 습기를 가두는 성장을 줄입니다." },
      { title: "홈통·downspout 청소", body: "처마와 배출 지점의 자유 배수를 복원합니다." },
      { title: "시각적 지붕 검토", body: "잔해 제거 후 드러난 덮개 손상을 기록합니다." },
    ],
    repairVsReplace: {
      repair: "서비스 가능한 지붕에서 성장·잔해가 주요 문제일 때 청소가 적합합니다.",
      replace: "청소 후 광범위한 부서짐이 드러나면 교체 계획이 더 정직할 수 있습니다.",
    },
    scopeFactors: ["지붕 면적", "경사·접근", "수목 밀도", "홈통 길이", "덮개 취약성", "잔해 처리"],
    process: [
      "지붕 유형, 그늘 노출 및 접근 정보 공유",
      "성장 범위, 배수 및 덮개 상태 현장 검토",
      "청소·처리·홈통 작업 서면 범위",
      "신중한 제거, 적절 시 처리 및 downspout flush",
      "수리가 필요한 항목 사진·메모",
    ],
    publicPriceHint:
      "범위는 지붕 면적, 경사, 성장 정도, 홈통 길이 및 접근에 따라 달라집니다. 평가를 요청하여 프로젝트별 범위를 확인하세요.",
    faqs: [
      {
        question: "이끼 제거 + 지붕·홈통 패키지란?",
        answer:
          "지붕 유기물, 홈통·downspout 청소 및 가시 상태 문서화를 결합한 유지보수 서비스입니다.",
      },
      {
        question: "고압 세척으로 이끼를 제거하는 것이 좋습니까?",
        answer:
          "아스팔트 싱글에는 과립을 벗기고 물을 덮개 아래로 몰 수 있어 일반적으로 권장되지 않습니다. 부드러운 기계적 방법과 자재에 맞는 처리가 더 낫습니다.",
      },
      {
        question: "이끼가 지붕을 실제로 손상합니까?",
        answer:
          "이끼는 덮개에 습기를 가두고 싱글 가장자리를 들어 올리며 과립 손실을 가속합니다. 조기 제거가 수리 비용을 크게 줄입니다.",
      },
      {
        question: "메트로 밴쿠버에서 이끼 제거는 얼마나 자주?",
        answer:
          "그늘, 수목 및 방향에 따라 다릅니다. 북향·심한 그늘은 재성장이 빠르므로 매트가 시작될 때 연 1회 점검이 많은 주택에 도움이 됩니다.",
      },
      {
        question: "이끼 제거로 홈통 넘침이 멈춥니까?",
        answer:
          "이끼 조각과 지붕 잔해가 홈통·downspout 막힘의 흔한 원인입니다. 이 패키지는 둘을 함께 정리합니다. 청소 후에도 넘치면 홈통 경사·크기를 검토해야 합니다.",
      },
    ],
  },
  "roof-health-check-maintenance": {
    title: "지붕 건강 점검 + 유지보수",
    shortTitle: "건강 점검",
    eyebrow: "상태 우선",
    description: "검사, 사진, 소규모 실란트·유지보수 및 계획을 위한 지붕 상태 보고.",
    definition:
      "지붕 건강 점검은 덮개, 플래싱, 배수 및 접근 가능한 부품의 구조화된 시각 평가와 사진·합의된 제한 유지보수입니다.",
    metaTitle: "지붕 건강 점검·유지보수 밴쿠버 | Revolin Roofing",
    metaDescription: "메트로 밴쿠버 지붕 건강 점검, 사진 및 유지보수 우선순위.",
    forWhom: "매수·매도, 개조, 초기 징후 또는 우기 전 기록이 필요한 소유자.",
    warningSigns: [
      "지붕 연식·상태 불확실",
      "원인 불명 작은 얼룩",
      "계절 잔해·이끼",
      "관통부 노후 실란트",
      "서면 기준 필요",
    ],
    evaluates: [
      "면별 덮개",
      "플래싱·관통부",
      "배수·홈통",
      "환기 부품",
      "유지보수 대 수리 우선순위",
    ],
    causes: [
      "문서화 없는 노화",
      "연기된 유지보수",
      "현장 노출",
      "품질 불명 이전 작업",
      "매수·개조 결정",
    ],
    methodology: [
      "우려·이력 검토",
      "외부 체계 검사",
      "대표 사진",
      "합의 소규모 유지보수만",
      "상태 요약·옵션 전달",
    ],
    materials: ["사진 문서", "범위 시 소규모 실란트·패스너", "범위 시 제한 잔해 청소", "서면 메모"],
    bcClimate: "가을 비 전·폭풍 후 점검이 배수 막힘과 초기 플래싱 실패를 잡는 데 유용합니다.",
    whenAppropriate: "수리·교체·추가 조사 전 명확성이 필요할 때.",
    whenOtherService: "활성 대형 누수는 긴급·수리 경로로.",
    solutionCategories: [
      { title: "문서화 검사", body: "보이고 접근 가능한 어셈블리의 구조화된 검토." },
      { title: "소규모 유지보수", body: "범위에 포함·적절할 때만 제한 작업." },
      { title: "계획 요약", body: "즉각 조치와 모니터링을 분리합니다." },
    ],
    repairVsReplace: {
      repair: "많은 발견은 유지보수·국소 수리입니다.",
      replace: "상태가 더 넓은 경로를 지원할 때만 교체 논의.",
    },
    scopeFactors: ["크기·복잡성", "접근", "문서화 깊이", "포함 유지보수", "지붕 시스템 수"],
    process: [
      "점검 예약·우려 설명",
      "사진·체계 외부 검사",
      "합의 소규모 유지보수",
      "우선순위 서면 요약",
      "권장 수리 후속",
    ],
    publicPriceHint:
      "범위는 크기·복잡성, 접근, 문서화 및 포함 유지보수에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "지붕 건강 점검 + 유지보수란?",
        answer: "덮개·플래싱·배수의 구조화된 시각 평가와 사진·합의 유지보수입니다.",
      },
      {
        question: "얼마나 자주 검사해야 합니까?",
        answer: "가을 비 전과 주요 강풍 후가 현지에서 유용합니다.",
      },
      {
        question: "점검 후 무엇을 받나요?",
        answer: "사진 문서 요약: 즉시 수리, 모니터링, 정상 마모로 구분된 계획 문서입니다.",
      },
      {
        question: "매수·매도 전 가치가 있나요?",
        answer: "문서화된 기준이 협상 불확실성을 줄입니다. 공식 홈 검사가 아닙니다.",
      },
      {
        question: "발견 문제를 고치나요?",
        answer: "합의된 소규모 유지보수는 방문 중 가능. 큰 발견은 별도 범위로 정합니다.",
      },
    ],
  },
  "flashing-roof-penetration-repair": {
    title: "플래싱·지붕 관통부 수리",
    shortTitle: "플래싱·관통부",
    eyebrow: "전환 및 인터페이스",
    description:
      "메트로 밴쿠버 지붕에서 물이 자주 들어오는 굴뚝, 벽, 통풍구, pipe boot 및 기타 관통부 플래싱 수리.",
    definition: "덮개가 벽, 굴뚝, 통풍구, 배관 및 관통부와 만나는 배수 인터페이스를 교정합니다.",
    metaTitle: "플래싱·관통부 수리 밴쿠버 | Revolin Roofing",
    metaDescription: "굴뚝, 벽, 통풍구 및 pipe boot 플래싱 수리. 원인 중심 평가.",
    forWhom: "굴뚝·사이드월·배관·통풍구 근처 누수 또는 노후 플래싱이 있는 소유자.",
    warningSigns: [
      "굴뚝·외벽 얼룩",
      "관통부 실란트 균열·경화",
      "들리거나 분리된 step·counter-flashing",
      "손상 pipe boot",
      "벽-지붕 접합부 바람 비 침수",
    ],
    evaluates: [
      "플래싱 기하·자재",
      "주변 덮개 호환",
      "counter-flashing·벽",
      "boot·collar",
      "실란트가 실패를 가리는지",
    ],
    causes: ["열 이동", "UV·풍화", "부적절 step·counter", "부식·손상", "관통부 미재세부 덮개 교체"],
    methodology: [
      "관통부·벽 식별",
      "필요 시만 개방",
      "호환 boot·금속 교체",
      "덮개 재통합",
      "표면 실리콘만 의존 안 함",
    ],
    materials: [
      "step·counter·apron",
      "pipe boot·collar",
      "underlayment·방수막",
      "해안 적합 금속",
      "2차 실란트",
    ],
    bcClimate: "바람 비와 습기 계절은 플래싱·boot에 스트레스를 줍니다.",
    whenAppropriate: "필드가 서비스 가능하고 인터페이스 실패가 국소적일 때.",
    whenOtherService: "주변 덮개 광범위 마모 또는 다른 전문 범위 필요 시.",
    solutionCategories: [
      { title: "boot 교체", body: "균열 전 pipe·통풍구 boot 교체." },
      { title: "벽·step 수리", body: "step·counter가 물을 못 흘릴 때 복원." },
      { title: "굴뚝·복합", body: "다면 플래싱 어셈블리 처리." },
    ],
    repairVsReplace: {
      repair: "필드 양호·인터페이스 집중 실패.",
      replace: "덮개 수명 말기 시 재통합.",
    },
    scopeFactors: [
      "관통부 수·유형",
      "벽 인터페이스",
      "금속 호환",
      "접근",
      "덮개 상태",
      "개방 범위",
    ],
    process: [
      "누수 위치 설명",
      "플래싱·관통부 검사",
      "서면 범위",
      "재세부 설계",
      "인터페이스 검토",
    ],
    publicPriceHint: "관통부·벽 복잡성 및 개방 범위에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "플래싱·관통부 수리란?",
        answer: "벽·굴뚝·통풍구·배관과 만나는 배수 인터페이스 교정입니다.",
      },
      {
        question: "플래싱 문제 징후?",
        answer: "관통부 근처·바람 비 중 누수는 흔히 플래싱 관련입니다.",
      },
      { question: "교체 없이 가능?", answer: "주변 덮개가 서비스 가능하면 보통 가능합니다." },
      {
        question: "실리콘만으로 왜 안 됩니까?",
        answer: "실란트는 금속보다 빨리 노화하고 기하학을 보완 못합니다.",
      },
      { question: "해안 BC 수명?", answer: "자재·노출에 따라 달라 단일 숫자는 정직하지 않습니다." },
    ],
  },
  "shingle-spot-repair": {
    title: "싱글 spot 수리",
    shortTitle: "싱글 spot",
    eyebrow: "국소 덮개 수리",
    description: "주변 필드가 서비스 가능할 때 누락·풍손·균열 아스팔트 싱글의 국소 수리.",
    definition: "매칭·주변 조건이 허용할 때 전체 재지붕 없이 제한된 손상 싱글을 교체·재밀착합니다.",
    metaTitle: "싱글 spot 수리 메트로 밴쿠버 | Revolin Roofing",
    metaDescription: "주변 필드가 양호한 메트로 밴쿠버 주택 싱글 spot 수리.",
    forWhom: "전반적으로 양호한 급경사 지붕의 국소 싱글 손상 소유자.",
    warningSigns: [
      "풍 후 누락 싱글",
      "균열 tab",
      "국소 underlayment 노출",
      "한 구역 과립",
      "들리는 이전 패치",
    ],
    evaluates: [
      "매칭 가능성",
      "주변 필드 유연성",
      "국소 underlayment",
      "손상 면 노출",
      "국소성 확인",
    ],
    causes: ["풍 uplift", "충격", "열 균열", "발판 손상", "밀착 스트립 실패"],
    methodology: [
      "매칭 확인",
      "손상 싱글 신중 제거",
      "국소 underlayment",
      "매칭 싱글 설치",
      "세부에 필요한 만 seal",
    ],
    materials: ["매칭 싱글", "국소 underlayment", "호환 패스너", "지정 sealant"],
    bcClimate: "해안 풍·습한 겨울은 deck 도달 전 timely spot 수리가 중요합니다.",
    whenAppropriate: "손상이 제한되고 필드가 호환 싱글을 받을 때.",
    whenOtherService: "광범위 부서짐·다면 실패는 아스팔트 교체 쪽.",
    solutionCategories: [
      { title: "국소 싱글 교체", body: "호환 자재로 손상 구역 복원." },
      { title: "가장자리·ridge touch-up", body: "국소 고스트레스 실패 교정." },
      { title: "누수 경로 확인", body: "spot 손상이 플래싱을 가리지 않는지 확인." },
    ],
    repairVsReplace: { repair: "필드 유연·매칭 가능.", replace: "마모가 systemic일 때." },
    scopeFactors: ["손상 면적", "경사·접근", "자재 매칭", "underlayment", "날씨 창"],
    process: [
      "손상 사진 전송",
      "국소성 지붕 확인",
      "프로필·색 매칭",
      "싱글 교체",
      "주변 필드 검토",
    ],
    publicPriceHint: "손상 면적, 경사·접근, 매칭에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "싱글 spot 수리란?",
        answer: "매칭·주변 조건이 허용할 때 제한 손상 싱글 교체·재밀착입니다.",
      },
      {
        question: "몇 개 날아가면 긴급?",
        answer: "underlayment가 노출되므로 다음 폭우 전 신속 수리가 좋습니다.",
      },
      {
        question: "색이 맞나요?",
        answer: "풍화 싱글과 새 싱글은 완벽 일치가 어렵습니다. 작업 전 기대를 논의합니다.",
      },
      { question: "몇 장까지 spot?", answer: "고정 수가 아니라 국소성·필드 유연성이 중요합니다." },
      { question: "균열에 seal만?", answer: "교체가 overlap·내구성을 복원하는 일반적 해결입니다." },
    ],
  },
  "gutter-cleaning-downspout-flush": {
    title: "홈통 청소 + downspout flush",
    shortTitle: "홈통 청소",
    eyebrow: "배수 유지보수",
    description: "메트로 밴쿠버 강우 중 처마 배수가 작동하게 홈통 청소 및 downspout flush.",
    definition: "홈통·downspout 잔해 제거로 지붕 유출수가 fascia·기초·조경으로 넘치지 않게 합니다.",
    metaTitle: "홈통 청소·downspout 밴쿠버 BC | Revolin Roofing",
    metaDescription: "메트로 밴쿠버 전문 홈통 청소 및 downspout flush.",
    forWhom: "넘침, 홈통 식물, 평상시 비 후 fascia 얼룩이 있는 소유자.",
    warningSigns: [
      "홈통 면에 물 흐름",
      "홈통 식물",
      "기초 splashback",
      "fascia 얼룩",
      "downspout 배수 불량",
    ],
    evaluates: ["잔해량", "홈통 pitch", "배출구·연장", "fascia 부착", "금속 실패 여부"],
    causes: ["잎·이끼", "침엽수 needle", "침하 pitch", "막힌 screen", "계절 청소 누락"],
    methodology: [
      "홈통 청소",
      "downspout flush",
      "배출 확인",
      "금속 상태 기록",
      "정당할 때만 guard·교체 권장",
    ],
    materials: ["청소 도구", "flush 장비", "범위 시 기본 패스너"],
    bcClimate: "잦은 비는 막힌 홈통이 fascia·기초를 빠르게 젖게 합니다.",
    whenAppropriate: "금속이 양호하고 잔해가 주요 문제일 때.",
    whenOtherService: "녹·분리· undersized 홈통은 교체가 필요할 수 있습니다.",
    solutionCategories: [
      { title: "홈통 청소", body: "유기·과립 잔해 제거." },
      { title: "downspout flush", body: "연속 배출 경로 복원." },
      { title: "상태 메모", body: "청소 중 실패 금속·fascia 기록." },
    ],
    repairVsReplace: {
      repair: "구조적으로 양호할 때 청소.",
      replace: "청소 후에도 성능 불가 시 교체.",
    },
    scopeFactors: ["연장 footage", "층수·접근", "잔해 유형", "downspout 수", "안전 요구"],
    process: [
      "높이·길이·접근 공유",
      "홈통 잔해 제거",
      "각 downspout flush·배수 확인",
      "금속·pitch·fascia 기록",
      "청소로 해결 안 되는 항목 보고",
    ],
    publicPriceHint: "연장, 층수, 잔해량 및 안전·접근에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "홈통 청소 + flush란?",
        answer: "홈통·downspout 잔해 제거로 처마 배수를 복원합니다.",
      },
      {
        question: "얼마나 자주?",
        answer: "가을 낙엽 후 최소 1회; 침엽수 많은 부지는 추가 방문이 흔합니다.",
      },
      {
        question: "건너뛰면?",
        answer: "넘침·fascia·기초 손상으로 작은 유지가 큰 수리로 바뀝니다.",
      },
      { question: "downspout도 flush?", answer: "둘 다 합니다. 막힌 downspout은 여전히 넘칩니다." },
      {
        question: "청소 후에도 넘치면?",
        answer: "pitch·크기 문제일 수 있습니다. 상태 메모와 교체 권장을 문서화합니다.",
      },
    ],
  },
  "storm-tarping-emergency-dry-in": {
    title: "폭풍 방수포·긴급 dry-in",
    shortTitle: "폭풍 dry-in",
    eyebrow: "임시 방수",
    description: "영구 작업 전 바람·나무·폭풍 손상 후 임시 방수.",
    definition:
      "폭풍 tarping·dry-in은 영구 수리 계획 중 내부 손상을 줄이기 위해 개구부·손상 구역을 임시 보호합니다.",
    metaTitle: "폭풍 방수포·긴급 dry-in 밴쿠버 | Revolin Roofing",
    metaDescription: "메트로 밴쿠버 폭풍·나무 손상 후 긴급 tarp·dry-in.",
    forWhom: "개방 deck, 큰 누락 면, 폭풍 후 새 개구부가 있는 소유자.",
    warningSigns: [
      "나무 충격 후 개방 deck",
      "큰 누락 면",
      "새 개구부로 실내 비",
      "불안정 잔해",
      "영구 자재 전 보호 필요",
    ],
    evaluates: ["안전·접근", "개구부 크기·위치", "내부 위험", "날씨 창", "영구 수리 경로"],
    causes: ["강풍", "나무 낙하", "급작 덮개 손실", "충격", "부분 노후 실패"],
    methodology: [
      "안전 평가 우선",
      "dry-in에 필요한 만 정리",
      "임시 방수 설치",
      "상태 문서화",
      "영구 경로 계획",
    ],
    materials: ["방수포·임시 막", "임시용 고정", "개구부·가장자리 보호"],
    bcClimate: "태평양 폭풍은 지붕을 빠르게 열 수 있습니다. dry-in은 2차 내부 손상을 줄입니다.",
    whenAppropriate: "영구 작업 전 개구부·불안정 구역을 방수해야 할 때.",
    whenOtherService: "dry-in은 영구 수리·교체 대체가 아닙니다.",
    solutionCategories: [
      { title: "임시 방수", body: "폭풍 개구부 침수 제한." },
      { title: "안정화", body: "느슨 자재 고정." },
      { title: "문서화", body: "영구 계획을 위한 기록." },
    ],
    repairVsReplace: {
      repair: "즉시 영구 불가 시 dry-in 우선.",
      replace: "어셈블리 복원 후 영구 수리·교체.",
    },
    scopeFactors: ["개구부 크기", "접근·높이", "날씨", "내부 노출", "잔해 위험"],
    process: [
      "사진·안전 정보 연락",
      "현장 위험·접근 평가",
      "필요 시 잔해 안정화",
      "임시 방수 고정",
      "영구 수리 계획",
    ],
    publicPriceHint:
      "개구부 크기, 높이, 작업 중 날씨 및 잔해 위험에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      { question: "폭풍 tarp·dry-in란?", answer: "영구 수리 전 개구부·손상 구역 임시 보호입니다." },
      {
        question: "나무가 지붕을 쳤을 때?",
        answer: "아래 공간 피하고 지붕 위로 가지 마세요. 전문 dry-in을 예약하세요.",
      },
      {
        question: "방수포는 얼마나?",
        answer: "임시용이며 주~수개월입니다. 가능한 빨리 영구 작업을 예약하세요.",
      },
      { question: "보험이 tarp를?", answer: "정책별입니다. Revolin은 조건·작업을 문서화합니다." },
      {
        question: "dry-in이 수리 대체?",
        answer: "아니요. 임시 보호이며 장기 방치는 재침수를 유발합니다.",
      },
    ],
  },
  "skylight-leak-flashing-repair": {
    title: "skylight 누수·플래싱 수리",
    shortTitle: "skylight 수리",
    eyebrow: "skylight 인터페이스",
    description: "누수 skylight 및 주변 루핑 수리·reflash.",
    definition: "skylight 누수 수리는 유리만이 아니라 curb, 플래싱 및 인접 루핑의 침수를 다룹니다.",
    metaTitle: "skylight 누수·플래싱 수리 밴쿠버 | Revolin Roofing",
    metaDescription: "메트로 밴쿠버 skylight curb 플래싱 수리.",
    forWhom: "skylight 아래 얼룩, 실패 curb 플래싱, 결로·누수 불확실 소유자.",
    warningSigns: [
      "well 얼룩",
      "light shaft 페인트 박리",
      "curb 모서리 빛",
      "실패 step flashing",
      "저경사 curb 침수",
    ],
    evaluates: [
      "skylight 유형·연식",
      "curb·플래싱",
      "인접 덮개 통합",
      "결로 패턴",
      "유닛 교체 필요성",
    ],
    causes: ["curb 플래싱 실패", "싱글·막과 부적절 통합", "노후 seal", "결로 오진", "개구부 이동"],
    methodology: [
      "누수·결로 구분",
      "curb·인접 검사",
      "호환 reflash",
      "주변 덮개 처리",
      "정당할 때만 유닛 교체",
    ],
    materials: ["curb 플래싱", "호환 underlayment·막", "주변 싱글·막 패치", "2차 sealant만"],
    bcClimate: "해안 BC 바람 비는 약한 curb를 빠르게 찾습니다.",
    whenAppropriate: "유닛이 서비스 가능하고 플래싱·인접 실패일 때.",
    whenOtherService: "프레임·유닛 전반 실패는 다른 범위.",
    solutionCategories: [
      { title: "curb reflash", body: "curb 금속·막 인터페이스 복원." },
      { title: "인접 덮개 수리", body: "유닛과 통합되지 않은 덮개 교정." },
      { title: "누수·결로 검토", body: "침수와 결로 증상 구분." },
    ],
    repairVsReplace: {
      repair: "유닛·curb가 reflash 가능.",
      replace: "어셈블리를 신뢰할 수 없을 때.",
    },
    scopeFactors: ["skylight 수·크기", "지붕 시스템", "curb 높이", "접근", "내부 마감 위험"],
    process: [
      "얼룩 패턴·연식 설명",
      "curb·플래싱 검사",
      "누수·결로 구분",
      "reflash·수리",
      "인터페이스 검증",
    ],
    publicPriceHint: "유닛별 curb, 시스템, 플래싱 및 접근에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "skylight 누수·플래싱 수리란?",
        answer: "curb·플래싱·인접 루핑의 침수를 다룹니다.",
      },
      {
        question: "누수인지 결로인지?",
        answer: "결로는 미세 물방울·안개; 누수는 curb·상단에서 얼룩이 흔합니다.",
      },
      {
        question: "새 skylight 필요?",
        answer: "플래싱·주변 루핑 원인이 많습니다. 이중창 fog 등은 교체 논의.",
      },
      {
        question: "재지붕 때 교체?",
        answer: "플래싱을 다시 해야 하므로 노후 유닛은 함께 검토합니다.",
      },
      { question: "바람 비에만 누수?", answer: "플래싱 기하 문제를 강하게 시사합니다." },
    ],
  },
  "chimney-flashing-repair": {
    title: "굴뚝 플래싱 수리",
    shortTitle: "굴뚝 플래싱",
    eyebrow: "굴뚝 인터페이스",
    description: "메트로 밴쿠버 지붕 step flashing, counter-flashing 및 굴뚝 관련 누수 교정.",
    definition: "벽돌·골조 굴뚝이 지붕 면과 만나는 금속 배수 인터페이스를 복원합니다.",
    metaTitle: "굴뚝 플래싱 수리 메트로 밴쿠버 | Revolin Roofing",
    metaDescription: "굴뚝 step·counter-flashing 수리.",
    forWhom: "굴뚝 모서리 누수, 녹슨 counter-flashing, deteriorated cricket 소유자.",
    warningSigns: [
      "굴뚝 모서리 얼룩",
      "counter-flashing 녹 streak",
      "mortar·누수 동반",
      "넓은 굴뚝 cricket 부재",
      "굴뚝 밑 tar",
    ],
    evaluates: [
      "굴뚝 크기·기하",
      "step·counter 상태",
      "upslope cricket",
      "masonry 인터페이스",
      "주변 덮개",
    ],
    causes: ["step 미짜넣기", "counter 분리", "열 이동", "cricket 부재", "노화·부식"],
    methodology: [
      "모든 면·교차 검사",
      "필요 시만 개방",
      "step·counter 올바르게 설치",
      "cricket 복원",
      "덮개 재통합",
    ],
    materials: ["step·counter", "cricket 자재", "underlayment", "적절 시 mortar·seal"],
    bcClimate: "굴뚝 모서리는 바람 비 고스트레스 구역입니다. tar는 금속 course 대체가 아닙니다.",
    whenAppropriate: "굴뚝·필드가 올바른 reflash를 지원할 때.",
    whenOtherService: "필드 수명 말기면 교체 계획과 함께.",
    solutionCategories: [
      { title: "step 복원", body: "덮개와 짜 넣은 금속 course." },
      { title: "counter 교정", body: "masonry·클래딩 termination 복원." },
      { title: "cricket·유도", body: "넓은 굴뚝 upslope 배수 개선." },
    ],
    repairVsReplace: {
      repair: "필드 서비스 가능 시 reflash.",
      replace: "필드가 통합 못 받을 때 reroof.",
    },
    scopeFactors: ["굴뚝 너비", "지붕 pitch", "masonry", "접근", "주변 덮개 연식"],
    process: [
      "굴뚝·얼룩 사진",
      "모든 면 검사",
      "금속 course 서면 범위",
      "step·counter·cricket 설치",
      "덮개 재통합",
    ],
    publicPriceHint:
      "굴뚝 너비, pitch, masonry 및 덮개 lift 범위에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "굴뚝 플래싱 수리란?",
        answer: "굴뚝·지붕 면 만남의 금속 배수 인터페이스 복원입니다.",
      },
      {
        question: "새 지붕인데 굴뚝 누수?",
        answer: "counter 재사용·step 미짜넣기가 흔한 원인입니다.",
      },
      {
        question: "counter-flashing이란?",
        answer: "step 위 masonry에 박힌 상부 금속 course입니다.",
      },
      { question: "cricket 필요?", answer: "넓은 굴뚝·downslope는 upslope 배수가 중요합니다." },
      {
        question: "tar로 고칠 수 있나요?",
        answer: "임시만 가능. 금속 course 재확립이 내구적입니다.",
      },
    ],
  },
  "gutter-replacement": {
    title: "홈통 교체",
    shortTitle: "홈통 교체",
    eyebrow: "처마 배수 시스템",
    description: "신뢰할 수 있는 처마 배수를 위한 seamless 알루미늄 홈통·downspout 교체.",
    definition:
      "건물 envelope에서 유출수를 멀리 보내기 위해 크기·pitch가 맞는 새 홈통·downspout을 설치합니다.",
    metaTitle: "홈통 교체 메트로 밴쿠버 | Revolin Roofing",
    metaDescription: "seamless 알루미늄 홈통·downspout 교체.",
    forWhom: "청소 후 만성 넘침, 녹·분리 홈통, fascia 손상 소유자.",
    warningSigns: [
      "seam 분리",
      "fascia에서 홈통 들림",
      "청소 후 넘침",
      "부식 hole",
      "pitch 교정 불가",
    ],
    evaluates: ["연장·층수", "배출구 설계", "fascia", "색·프로필", "downspout 경로"],
    causes: ["금속 수명 말기", "undersized", "fascia 이동", "잔해", "부적절 설치"],
    methodology: [
      "fascia 준비 확인",
      "pitch·배출구 layout",
      "seamless 설치",
      "유량 테스트",
      "splash·배출 처리",
    ],
    materials: ["seamless 알루미늄", "downspout·elbow", "hanger·패스너", "end cap·outlet"],
    bcClimate: "강우량은 올바른 홈통 크기·pitch를 필수로 합니다.",
    whenAppropriate: "청소·소수리 후에도 성능 불가.",
    whenOtherService: "양호하면 청소·guard로 충분할 수 있습니다.",
    solutionCategories: [
      { title: "seamless 처마", body: "controlled outlet로 pitch된 trough." },
      { title: "downspout 설계", body: "기초·보도에서 배출 멀리." },
      { title: "fascia 조정", body: "필요 시 fascia 먼저 처리." },
    ],
    repairVsReplace: { repair: "금속·기하 실패 시 교체.", replace: "양호하면 청소·수리." },
    scopeFactors: ["연장", "높이·접근", "outlet 수", "색", "fascia 수리"],
    process: [
      "run·outlet 측정",
      "fascia hanger 지지 확인",
      "프로필·색 서면 범위",
      "현장 forming·설치",
      "유량·배출 검토",
    ],
    publicPriceHint:
      "연장, 층수, outlet 수 및 fascia 선행 수리에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      { question: "홈통 교체란?", answer: "크기·pitch가 맞는 새 홈통·downspout 설치입니다." },
      {
        question: "seamless란?",
        answer: "현장 연속 forming으로 seam을 outlet·모서리로만 줄입니다.",
      },
      {
        question: "지붕과 함께?",
        answer: "둘 다 말기면 함께 하는 것이 tear-off·위치 조정에 유리합니다.",
      },
      { question: "크기는?", answer: "drain 면적·pitch·outlet에 따라 주택별로 다릅니다." },
      { question: "기초 배수?", answer: "extension·splash·지면 배수와 연계 계획합니다." },
    ],
  },
  "gutter-guard-installation": {
    title: "홈통 guard 설치",
    shortTitle: "홈통 guard",
    eyebrow: "잔해 보호",
    description: "수목 많은 부지의 잎·이끼·잔해 clog 빈도를 줄이는 보호 시스템.",
    definition: "물은 들어가고 잔해는 줄이기 위해 홈통 위 보호 시스템을 추가합니다.",
    metaTitle: "홈통 guard 설치 메트로 밴쿠버 | Revolin Roofing",
    metaDescription: "heavy 잔해 부지 홈통 guard 설치.",
    forWhom: "연간 여러 번 청소, 다층 접근 어려움, 수목 cover 소유자.",
    warningSigns: [
      "연간 다회 청소",
      "다층 접근 어려움",
      "needle 많은 수목",
      "청소 직후 clog",
      "유지 접근 제한",
    ],
    evaluates: ["홈통 구조 먼저", "잔해 profile", "제품 적합성", "지붕 edge 기하", "유지 기대"],
    causes: [
      "dense 조경",
      "지붕 기하가 trough에 잔해",
      "fine debris가 screen 무력화",
      "불규칙 유지",
    ],
    methodology: [
      "홈통 건전 확인",
      "적합 제품 선택",
      "water entry·edge 주의 설치",
      "유량 테스트",
      "잔여 유지 설명",
    ],
    materials: ["guard 부품", "호환 패스너", "edge accessory"],
    bcClimate: "needle·이끼 fragment는 guard가 줄이지만 제로 유지는 아닙니다.",
    whenAppropriate: "홈통이 양호하고 잔해가 주요 문제.",
    whenOtherService: "실패 홈통은 guard 전 교체.",
    solutionCategories: [
      { title: "잔해 보호", body: "잎·needle 유입 감소." },
      { title: "호환 확인", body: "배수 가능한 홈통에만." },
      { title: "유지 기대", body: "guard가 줄이는 것·여전히 필요한 것." },
    ],
    repairVsReplace: { repair: "양호 홈통·heavy 잔해.", replace: "금속 실패 시 먼저 교체." },
    scopeFactors: ["연장", "제품 유형", "잔해 profile", "접근", "기존 홈통"],
    process: [
      "홈통·잔해 profile 평가",
      "수목에 맞는 guard 추천",
      "선행 청소 포함 서면 범위",
      "guard 설치",
      "잔여 유지 rhythm 설명",
    ],
    publicPriceHint: "제품, 연장, edge 기하 및 홈통 상태에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      { question: "홈통 guard란?", answer: "잔해를 줄이되 물은 들어가게 하는 보호 시스템입니다." },
      { question: "청소 영원히 안?", answer: "아니요. 빈도는 줄지만 위·outlet 점검은 필요합니다." },
      { question: "pine needle?", answer: "micro-mesh가 더 낫지만 가끔 브러싱이 필요합니다." },
      { question: "기존 홈통에?", answer: "양호·pitch·부착 확인 후 가능합니다." },
      { question: "폭우 overshoot?", answer: "제품·pitch·eave 유속이 선택에 영향을 줍니다." },
    ],
  },
  "roof-ventilation-upgrade": {
    title: "지붕 환기 업그레이드",
    shortTitle: "환기 업그레이드",
    eyebrow: "다락방 airflow",
    description: "box vent, ridge vent, soffit intake 및 다락방 airflow 교정.",
    definition: "intake·exhaust 경로를 개선하여 다락방이 습기·열을 더 잘 관리하게 합니다.",
    metaTitle: "지붕 환기 업그레이드 메트로 밴쿠버 | Revolin Roofing",
    metaDescription: "soffit intake·exhaust 균형 환기 업그레이드.",
    forWhom: "다락방 결로, uneven 싱글 노화, blocked soffit, exhaust 부족 소유자.",
    warningSigns: [
      "다락방 결로·서리",
      "musty 냄새",
      "uneven 싱글 노화",
      "blocked soffit",
      "뜨거운 다락방",
    ],
    evaluates: ["다락 구성", "exhaust vent", "intake 연속성", "eave 단열", "지붕 기하 제약"],
    causes: [
      "exhaust 부족",
      "intake 막힘",
      "soffit 덮은 단열",
      "fan이 다락에 dump",
      "환기 없는 reroof",
    ],
    methodology: [
      "intake·exhaust balance",
      "soffit intake 복원",
      "exhaust 추가·교정",
      "intake 없이 over-vent 피함",
      "단열·air seal 연계",
    ],
    materials: ["ridge·box 등 exhaust", "soffit intake", "baffle", "새 관통 플래싱"],
    bcClimate: "서늘·습한 BC는 다락방 습기 관리가 중요합니다. 환기는 누수 치료가 아닙니다.",
    whenAppropriate: "airflow 불균형이 습기·덮개 마모에 기여할 때.",
    whenOtherService: "환기는 누수 수리 대체가 아닙니다.",
    solutionCategories: [
      { title: "exhaust 업그레이드", body: "ridge·box 등 추가·교정." },
      { title: "intake 복원", body: "blocked soffit 개선." },
      { title: "balance 검토", body: "다락 volume에 맞는 전략." },
    ],
    repairVsReplace: {
      repair: "불균형이 문서화될 때 업그레이드.",
      replace: "방수 수리 대체 아님.",
    },
    scopeFactors: ["다락 volume", "기존 vent 유형", "intake 가용", "덮개 유형", "접근"],
    process: [
      "다락·외부 vent 검토",
      "balance 평가",
      "vent·intake·baffle 서면 범위",
      "설치·플래싱",
      "경로 unobstructed 확인",
    ],
    publicPriceHint: "다락 volume, vent 유형, intake 및 덮개에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      { question: "지붕 환기 업그레이드란?", answer: "intake·exhaust 경로를 개선하는 작업입니다." },
      { question: "습기 징후?", answer: "겨울 sheathing 결로, musty, uneven 싱글 노화 등." },
      {
        question: "vent만 더하면?",
        answer: "intake 없으면 역효과일 수 있습니다. 전체 경로를 봅니다.",
      },
      {
        question: "ridge vent vs box?",
        answer: "ridge는 연속 peak exhaust; box는 discrete. 기하에 따라 선택.",
      },
      { question: "싱글 수명?", answer: "열·습기 부담을 줄이지만 설치·자재도 중요합니다." },
    ],
  },
  "fascia-soffit-repair-replacement": {
    title: "fascia + soffit 수리·교체",
    shortTitle: "fascia·soffit",
    eyebrow: "처마 부품",
    description: "rotten fascia, soffit 및 홈통·edge를 지지하는 처마 부품 수리·교체.",
    definition:
      "지붕 edge를 마감하고 홈통을 지지하고 soffit intake에 기여하는 처마 어셈블리를 복원합니다.",
    metaTitle: "fascia·soffit 수리 밴쿠버 | Revolin Roofing",
    metaDescription: "fascia·soffit 수리·교체.",
    forWhom: "soft fascia, failing soffit, 홈통 분리, pest 개구 소유자.",
    warningSigns: [
      "soft·rotten fascia",
      "sagging soffit",
      "홈통 bracket 들림",
      "페인트·기재 decay",
      "pest 개구",
    ],
    evaluates: ["손상 연장", "자재 선택", "soffit 환기", "홈통 재부착", "페인트·finish"],
    causes: ["홈통 overflow", "실패 edge metal", "자재 수명", "환기 습기", "충격·개조"],
    methodology: [
      "손상 길이 mapping",
      "실패 부품 제거",
      "새 fascia·soffit",
      "intake 개구 복원",
      "홈통 조정",
    ],
    materials: ["fascia board·cladding", "soffit panel", "패스너·trim", "범위 시 finish"],
    bcClimate: "우기 홈통 overflow는 fascia decay의 흔한 원인입니다.",
    whenAppropriate: "처마 손상이지만 필드가 서비스 가능할 때.",
    whenOtherService: "홈통·edge 작업과 연계할 때.",
    solutionCategories: [
      { title: "fascia 복원", body: "홈통·edge metal 지지 board 교체." },
      { title: "soffit 수리", body: "eave underside·intake 복원." },
      { title: "eave 조정", body: "홈통·edge와 순서 조정." },
    ],
    repairVsReplace: { repair: "국소 손상.", replace: "연속 decay along eave." },
    scopeFactors: ["연장", "자재", "높이·접근", "finish", "홈통 연계"],
    process: [
      "손상 eave mapping",
      "홈통 detach·rehang 계획",
      "자재·finish 서면 범위",
      "decay 제거·설치",
      "intake·홈통 복원",
    ],
    publicPriceHint: "연장, 접근, 자재·finish 및 홈통 연계에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "fascia·soffit 수리·교체란?",
        answer: "edge 마감·홈통 지지·soffit intake 처마 복원입니다.",
      },
      {
        question: "fascia rot 원인?",
        answer: "홈통 overflow·실패 drip edge가 우기에 반복 젖게 합니다.",
      },
      { question: "홈통 없이 fascia?", answer: "거의 불가. detach·rehang이 올바른 교체입니다." },
      {
        question: "soffit이 환기?",
        answer:
          "대부분 intake opening을 담당합니다. cosmetic만 바꾸면 습기 문제가 생길 수 있습니다.",
      },
      { question: "wood vs aluminum vs composite?", answer: "노출·예산·건축에 따라 선택합니다." },
    ],
  },
  "cedar-shake-repair-restoration": {
    title: "cedar shake 수리·복원",
    shortTitle: "cedar 수리",
    eyebrow: "프리미엄 목재 지붕",
    description: "손상 shake 교체 및 프리미엄 목재 지붕 국소 청소·복원.",
    definition: "broader cedar 지붕이 유지 가치가 있을 때 실패 shake·세부 사항을 복원합니다.",
    metaTitle: "cedar shake 수리 메트로 밴쿠버 | Revolin Roofing",
    metaDescription: "cedar shake 국소 수리·복원.",
    forWhom: "국소 split·missing·cupped shake가 있는 cedar 소유자.",
    warningSigns: [
      "shake split·missing",
      "국소 cupping",
      "이끼 습기",
      "fastener stain·국소 실패",
      "저경사 debris dam",
    ],
    evaluates: ["매칭 shake", "잔여 field", "청소 필요", "플래싱", "교체가 더 가까운지"],
    causes: [
      "UV·습윤 cycle",
      "biological growth",
      "충격",
      "부적절 이전 수리",
      "유지 가치 있는 노화",
    ],
    methodology: [
      "매칭 shake 확보",
      "실패 unit 교체",
      "습기 잔해·growth 처리",
      "작업 구역 플래싱",
      "잔여 수명 기록",
    ],
    materials: ["적절 grade cedar shake", "underlayment patch", "호환 패스너", "목재 적합 청소"],
    bcClimate: "습한 BC는 목재에 가혹합니다. field가 양호하면 유지가 수명을 연장합니다.",
    whenAppropriate: "실패가 국소적이고 field가 maintainable.",
    whenOtherService: "system-wide cupping·underlayment 실패는 cedar 교체.",
    solutionCategories: [
      { title: "선택 shake 교체", body: "maintainable field 보존." },
      { title: "국소 청소", body: "습기 잔해·growth 감소." },
      { title: "상태 triage", body: "유지 vs 교체 구분." },
    ],
    repairVsReplace: { repair: "field 유지 가치 있을 때.", replace: "wood wear systemic." },
    scopeFactors: ["수리 면적", "shake match", "접근", "청소 범위", "인접 플래싱"],
    process: [
      "field·growth·실패 위치 평가",
      "grade shake 확보",
      "수리·청소 서면 범위",
      "shake 교체·잔해 처리",
      "잔여 수명 문서화",
    ],
    publicPriceHint:
      "수리 면적, shake matching, 접근·청소·플래싱에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "cedar shake 수리·복원란?",
        answer: "broader cedar가 유지 가치 있을 때 국소 shake·세부 복원입니다.",
      },
      { question: "수리 vs 교체?", answer: "field·underlayment 상태가 결정합니다." },
      {
        question: "cupping·split 이유?",
        answer: "습윤·건조 cycle, 그늘·이끼, grade·고정이 영향을 줍니다.",
      },
      { question: "처리로 수명?", answer: "이끼·잔해 제거가 이 기후에서 최우선 유지입니다." },
      { question: "색 매칭?", answer: "새 cedar는 시간이 지나며 weathering됩니다." },
    ],
  },
  "flat-low-slope-roof-repair": {
    title: "평지·저경사 지붕 수리",
    shortTitle: "평지 수리",
    eyebrow: "저경사 막",
    description: "SBS, torch-on, TPO/EPDM patching, 플래싱·관통부 저경사 수리.",
    definition: "전체 저경사 교체 없이 막 seam, 플래싱, 관통부 및 국소 막 실패를 다룹니다.",
    metaTitle: "평지 지붕 수리 메트로 밴쿠버 | Revolin Roofing",
    metaDescription: "SBS, TPO, EPDM 저경사 막·플래싱 수리.",
    forWhom: "침수 seepage, failed seam, curb leak, open 막 저경사 소유자.",
    warningSigns: [
      "seam 개방",
      "puncture",
      "failed pitch pan",
      "active blister seepage",
      "drain·scupper 불량",
    ],
    evaluates: ["막 유형", "patch 호환", "배수 패턴", "관통부", "국소 넘어 젖은 단열"],
    causes: [
      "막 노화",
      "foot traffic",
      "침수 stress",
      "parapet flashing 이동",
      "부호환 이전 patch",
    ],
    methodology: [
      "막 시스템 식별",
      "호환 patch 방법",
      "플래싱·관통 수리",
      "국소 배수 개선",
      "소진 시 교체 권장",
    ],
    materials: ["호환 막 patch", "플래싱 금속", "penetration pocket", "시스템 적합 접착·열"],
    bcClimate: "긴 우기·침수는 seam·drain 세부가 핵심입니다.",
    whenAppropriate: "국소 실패·막 field가 durable patch 가능.",
    whenOtherService: "포화 단열·system-wide seam 실패는 교체.",
    solutionCategories: [
      { title: "막 patching", body: "seam·puncture·field 국소 수리." },
      { title: "관통·curb", body: "옥상 개구부 방수 복원." },
      { title: "배수 교정", body: "물이 약점에 머무는 세부 처리." },
    ],
    repairVsReplace: { repair: "막이 broadly viable.", replace: "포화·반복 실패 시 교체." },
    scopeFactors: ["막 유형", "수리 면적", "접근", "관통부 수", "배수"],
    process: [
      "막·실패점 식별",
      "습기·배수 검토",
      "patch·세부 서면 범위",
      "시스템 적합 수리",
      "배수·문서 검토",
    ],
    publicPriceHint: "막 유형, 면적, 관통부·배수·접근에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "평지·저경사 수리란?",
        answer: "전체 교체 없이 막·플래싱·관통부 국소 실패를 다룹니다.",
      },
      {
        question: "막 유형이 왜 중요?",
        answer: "SBS, TPO, EPDM은 patch 방법이 다릅니다. 오인 patch는 재누수를 유발합니다.",
      },
      {
        question: "침수(ponding)?",
        answer: "seam·노화를 가속합니다. drain·국소 low spot은 수리·교체 논의로 갈립니다.",
      },
      {
        question: "겨울 수리?",
        answer: "응급 안정화는 가능하지만 일부 영구 detail은 날씨 창이 필요할 수 있습니다.",
      },
      {
        question: "단열이 젖었는지?",
        answer: "blister·soft spot 등으로 의심; patch 위 젖은 단열은 deterioration을 계속합니다.",
      },
    ],
  },
  "metal-roof-repair-maintenance": {
    title: "금속 지붕 수리·유지보수",
    shortTitle: "금속 수리",
    eyebrow: "금속 시스템",
    description: "패스너, 플래싱, sealant, 패널·관통부 금속 지붕 수리.",
    definition: "서비스 가능한 패널을 유지하며 패스너, 플래싱, sealant, 패널·관통부를 교정합니다.",
    metaTitle: "금속 지붕 수리 메트로 밴쿠버 | Revolin Roofing",
    metaDescription: "패스너, 플래싱, closure, 관통부 금속 수리.",
    forWhom: "loose fastener, failed closure, 관통 누수, 국소 패널 손상 소유자.",
    warningSigns: [
      "backed-out fastener",
      "failed foam closure",
      "collar leak",
      "detail sealant crack",
      "cut-edge 부식 시작",
    ],
    evaluates: ["패널 profile", "coating", "fastener 유형", "closure·플래싱", "systemic 여부"],
    causes: ["열 cycling", "부적절 fastener", "sealant 노화", "충격 dent", "해안 노출"],
    methodology: [
      "시스템 유형 식별",
      "fastener·closure 교정",
      "플래싱·관통 수리",
      "국소 패널 손상",
      "cosmetic sealant-only 피함",
    ],
    materials: ["호환 fastener·washer", "closure strip", "플래싱 금속", "적절 시 touch-up"],
    bcClimate: "해안·열 이동은 fastener·플래싱을 recurring point로 만듭니다.",
    whenAppropriate: "패널이 sound하고 component-level 이슈.",
    whenOtherService: "system-wide coating·obsolete profile은 교체 논의.",
    solutionCategories: [
      { title: "fastener·closure", body: "기계적 weather detail 복원." },
      { title: "플래싱·관통", body: "침수 인터페이스 교정." },
      { title: "패널 수준", body: "sound field에서 discrete 손상." },
    ],
    repairVsReplace: { repair: "패널 sound·component 이슈.", replace: "system-wide coating 실패." },
    scopeFactors: ["profile", "coating", "fastener", "closure", "접근"],
    process: [
      "시스템·실패 식별",
      "fastener·closure 검토",
      "플래싱·관통 서면 범위",
      "수리 실행",
      "weather detail 검토",
    ],
    publicPriceHint:
      "profile, coating, fastener·closure 및 접근에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "금속 지붕 수리·유지보수란?",
        answer: "서비스 가능 패널을 유지하며 component를 교정합니다.",
      },
      {
        question: "노출 fastener 유지?",
        answer: "gasketed screw는 thermal cycling에 loosen·경화합니다. 정기 점검이 흔합니다.",
      },
      {
        question: "vent pipe 누수?",
        answer: "관통부가 흔한 leak point; boot 교체로 해결되는 경우가 많습니다.",
      },
      { question: "dent 수리?", answer: "coating·seam 손상 없으면 cosmetic; 손상 시 sheet 교체." },
      {
        question: "standing seam vs screw-down?",
        answer: "standing seam은 clip 고정; screw-down은 fastener 점검이 routine.",
      },
    ],
  },
  "cedar-shake-roof-replacement": {
    title: "cedar shake 지붕 교체",
    shortTitle: "cedar 교체",
    eyebrow: "프리미엄 목재 재지붕",
    description: "목재 지붕을 의도적 건축 선택으로 유지하는 주택의 cedar shake 전체 재지붕.",
    definition:
      "cedar shake 또는 지정 목재 부품으로 기판 준비와 올바른 세부를 포함한 전체 재지붕입니다.",
    metaTitle: "cedar shake 지붕 교체 밴쿠버 BC | Revolin Roofing",
    metaDescription: "메트로 밴쿠버 cedar shake 전체 재지붕.",
    forWhom: "system-wide cedar wear로 수리가 비합리적이지만 wood character를 유지하려는 소유자.",
    warningSigns: [
      "system-wide cupping·split",
      "다면 반복 leak",
      "underlayment 실패",
      "잔여 수명 우려",
      "wood 지붕 의도",
    ],
    evaluates: [
      "shake grade·profile",
      "underlayment 전략",
      "환기·건조",
      "프로젝트 맥락",
      "기하 복잡성",
    ],
    causes: ["수명 완료", "만성 습기", "다면 storm loss", "시간에 따른 기판 이슈"],
    methodology: [
      "기판·어셈블리 평가",
      "shake·underlayment 명세",
      "tear-off 보호",
      "완전 wood assembly 설치",
      "cedar 유지 기대 검토",
    ],
    materials: ["지정 grade cedar", "underlayment", "wood 호환 플래싱", "패스너·ridge detail"],
    bcClimate:
      "프리미엄 wood는 정직한 유지 기대가 필요합니다. 수리가 따라가지 못할 때 교체가 reset입니다.",
    whenAppropriate: "완전 cedar 시스템을 원하고 coherent service가 끝났을 때.",
    whenOtherService: "strong field의 국소 실패는 cedar 수리.",
    solutionCategories: [
      { title: "cedar field 설치", body: "coherent shake 시스템." },
      { title: "기판·underlayment", body: "wood 아래 배수층." },
      { title: "wood 세부", body: "cedar 성능을 위한 플래싱·edge." },
    ],
    repairVsReplace: { repair: "국소만.", replace: "systemic wear·wood 선택 유지." },
    scopeFactors: ["면적", "shake 명세", "기판 수리", "접근", "복잡성"],
    process: [
      "기존 wood·기판 평가",
      "grade·underlayment 명세",
      "서면·보호 계획",
      "tear-off·설치",
      "완료·유지 가이드",
    ],
    publicPriceHint: "면적·기하, shake 명세, 기판 수리·접근에 따라 달라집니다. 평가를 요청하세요.",
    faqs: [
      {
        question: "cedar shake 교체란?",
        answer: "기판 준비·올바른 세부를 포함한 cedar 전체 재지붕입니다.",
      },
      {
        question: "BC에서 cedar 수명?",
        answer: "grade·노출·유지에 따라 넓게 달라 단일 숫자는 misleading입니다.",
      },
      {
        question: "shake vs shingle?",
        answer: "shake는 split·두껍고 textured; shingle은 sawn·얇습니다. 명세 중 결정합니다.",
      },
      {
        question: "특수 underlayment?",
        answer: "wood는 breathe해야 하므로 interlayment·eave 보호가 typical입니다.",
      },
      { question: "밴쿠버에서 cedar?", answer: "건축·유지 commitment가 있는 프리미엄 선택입니다." },
    ],
  },
} satisfies Record<string, ServiceContent>;

export const serviceAreas = {
  vancouver: {
    intro:
      "밴쿠버 주택은 소형 단독 주택부터 증축, 데크 및 저경사 구역을 갖춘 건축가가 설계한 주택까지 다양합니다. 유용한 지붕 평가는 모든 것에 적합한 단일 시스템 권장 사항이 아닌 건물의 실제 형상으로 시작됩니다.",
    localContext:
      "밴쿠버 시 내의 프로젝트에는 브리티시 컬럼비아의 다른 곳에서 사용되는 요구 사항과 다른 요구 사항이 적용될 수 있습니다. Revolin은 범위를 확정하기 전에 부동산 관할권 및 관련 프로젝트 요구 사항을 확인합니다.",
    assessmentFocus: [
      "원래 지붕, 추가 부분 및 낮은 경사 부분 사이의 전환",
      "계곡, 벽 교차점, 관통부 및 주변 후레싱",
      "밀집된 도시 부지 내에서 작동해야 하는 배수로",
    ],
    propertyContexts: ["단독주택", "개조 및 확장된 주택", "혼합 경사 지붕 시스템"],
    faqs: [
      {
        question: "밴쿠버 지붕 프로젝트는 BC의 나머지 지역과 동일한 규칙을 따르나요?",
        answer:
          "항상 그런 것은 아닙니다. 밴쿠버 시에는 자체 건축 조례 체계가 있습니다. 적용 가능한 요구 사항은 주소와 범위에 따라 다르므로 지역 체크리스트에서 가정하는 것이 아니라 계획 중에 확인됩니다.",
      },
      {
        question: "한 집에 두 가지 이상의 지붕 접근 방식이 필요할 수 있습니까?",
        answer:
          "예. 추가, 현관 및 지붕 데크는 주 지붕과 다른 경사 및 전환을 도입할 수 있습니다. 평가에서는 이러한 연결을 하나의 물 관리 시스템으로 간주해야 합니다.",
      },
    ],
  },
  burnaby: {
    intro:
      "버나비의 주거 지역에는 평지, 언덕 거리, 증축을 통해 발전한 주택이 포함됩니다. 이러한 다양성은 배수, 안전한 접근 및 지붕 부분 간의 연결을 첫 번째 평가의 중요한 부분으로 만듭니다.",
    localContext:
      "버나비 지붕은 노출, 나무 덮개, 경사 및 건물 형태가 현장에 따라 다르기 때문에 단지 몇 동네만 떨어진 다른 지붕과 다르게 작동할 수 있습니다. 추천 사항은 숙소에서 관찰한 조건을 바탕으로 작성되었습니다.",
    assessmentFocus: [
      "집 주변의 경사, 접근 및 자재 취급 제약",
      "계곡, 처마, 홈통 및 낮은 지붕 부분의 배수",
      "추가 건물이 원래 건물과 만나는 연결부",
    ],
    propertyContexts: ["힐사이드 부동산", "추가된 주택", "성숙한 주거용 부지"],
    faqs: [
      {
        question: "현장 접근이 루핑 범위에 중요한 이유는 무엇입니까?",
        answer:
          "접근은 보호, 자재 이동 및 작업 순서 지정 방법에 영향을 미칩니다. 설치 계획과 범위가 확정되기 전에 이를 검토해야 합니다.",
      },
      {
        question: "배수구를 지붕과 함께 검토해야 합니까?",
        answer:
          "배수는 지붕 표면에서 적절한 배수 지점까지 연결된 경로로 간주되어야 합니다. 평가 문서에는 선택적인 배수 작업이 포함되어 있다는 가정 없이 눈에 보이는 홈통과 빗물홈통 상태가 기록되어 있습니다.",
      },
    ],
  },
  coquitlam: {
    intro:
      "코퀴틀람 부동산은 경사진 부지, 다중 평면 주택 및 인근 식물을 결합할 수 있습니다. 수리 또는 교체를 권장하기 전에 지붕 상태, 잔해 경로, 배수 및 바람에 의한 비 노출을 함께 검토합니다.",
    localContext:
      "코퀴틀람 전역에서 인근 지역의 고도와 노출이 변경될 수 있습니다. 따라서 적절한 지붕 시스템과 세부 사항은 도시 전체의 자재 처방이 아닌 개별 건물에 따라 달라집니다.",
    assessmentFocus: [
      "다층 주택의 상부에서 하부 지붕 배수",
      "복잡한 지붕 형태의 계곡 및 지붕에서 벽으로의 전환",
      "홈통, 관통부 및 배수 지점 주변에 잔해물 축적",
    ],
    propertyContexts: ["다층 주택", "경사진 주거지", "성숙한 식물 근처의 주택"],
    faqs: [
      {
        question: "복잡한 지붕은 자동으로 교체가 필요합니까?",
        answer:
          "아니요. 복잡성은 진단 및 세부 사항에 영향을 미치지만 수리와 교체는 여전히 관찰된 문제가 고립되어 있는지 또는 시스템 전체에 걸쳐 있는지에 따라 달라집니다.",
      },
      {
        question: "검사 중 나무 잔해는 어떻게 고려됩니까?",
        answer:
          "평가를 통해 잔해물이 쌓이거나 배수 속도를 늦추는 영역을 문서화할 수 있습니다. 그런 다음 유지 관리 권장 사항은 해당 건물에 보이는 것과 연결됩니다.",
      },
    ],
  },
  surrey: {
    intro:
      "써리(Surrey)는 기존 지역부터 크고 연결식 지붕 형태를 갖춘 새 주택까지 광범위한 주거 환경을 다루고 있습니다. 지붕 면적, 노출 및 전환 횟수는 모두 실제 범위에 영향을 미칩니다.",
    localContext:
      "도시마다 조건이 다르기 때문에 한 서리 부동산에 대한 추천을 다른 부동산의 표준 사양으로 간주해서는 안 됩니다. Revolin은 우리 앞에 있는 지붕을 문서화하고 장단점을 설명합니다.",
    assessmentFocus: [
      "대형 지붕면과 그 사이에 물이 흐르는 방식",
      "지붕창, 벽, 굴뚝 및 관통부에서 플래싱",
      "손상이 지붕 시스템 전체에 걸쳐 고립되거나 반복되는지 여부",
    ],
    propertyContexts: ["대형 단독주택", "최신 다중 평면 지붕", "동네 주택을 설립했습니다."],
    faqs: [
      {
        question: "대형 지붕의 수리와 교체를 결정하는 것은 무엇입니까?",
        answer:
          "크기만으로는 결정되지 않습니다. 결정은 커버 상태, 고장 횟수 및 위치, 전환 세부 사항 및 내구성 있는 국지적 수리가 가능한지 여부에 따라 달라집니다.",
      },
      {
        question: "평가를 하나의 문제 영역으로 제한할 수 있습니까?",
        answer:
          "보고된 문제는 면밀히 조사되었지만 물이 실내에서 눈에 보이기 전에 이동할 수 있으므로 인접한 지붕 면과 배수 경로도 검토해야 합니다.",
      },
    ],
  },
  richmond: {
    intro:
      "리치몬드의 일반적으로 저지대 환경은 통제된 지붕 배수를 특히 장기간 비가 내리는 동안 눈에 띄게 만듭니다. 낮은 경사 부분, 주변 가장자리 및 배출 경로는 기본 지붕 덮개와 동일한 주의를 기울일 가치가 있습니다.",
    localContext:
      "해안 노출과 현장 개방성은 바람에 의한 비가 지붕 가장자리와 전환에 도달하는 방식에 영향을 미칠 수 있습니다. 자재 및 세부 사항 권장 사항은 주택의 경사, 형태 및 노출을 검토한 후에만 결정됩니다.",
    assessmentFocus: [
      "낮은 경사면의 배수가 잘되고 눈에 보이는 낮은 지점",
      "폭우에 노출된 지붕 가장자리, 후레싱 및 전환",
      "홈통과 수직홈통이 지붕면에서 물을 받는 방법",
    ],
    propertyContexts: ["낮은 경사 지붕 부분", "개방형 주거지", "현대 단독 주택"],
    faqs: [
      {
        question: "평평해 보이는 지붕이 실제로는 평평할까요?",
        answer:
          "경사가 낮은 지붕이라도 물이 배수구 쪽으로 향해야 합니다. 평가에서는 설계된 배수 경로와 물이 의도한 대로 움직이지 않는다는 가시적 징후를 찾습니다.",
      },
      {
        question: "해안 노출은 금속이 항상 올바른 선택이라는 것을 의미합니까?",
        answer:
          "아니요. 지붕 경사, 조립 설계, 제품 적합성, 세부 사항 및 주택 소유자 우선 순위가 모두 중요합니다. 위치에서만 재료가 선택되지 않습니다.",
      },
    ],
  },
  "north-vancouver": {
    intro:
      "노스 밴쿠버 주택은 종종 가파르고 습하며 식물이 무성한 환경에 위치합니다. 안전한 접근, 집중 유출수 및 배수 지점 주변의 잔해물은 지붕 작업 범위를 지정하기 전에 실질적인 고려 사항입니다.",
    localContext:
      "조건은 낮은 지역과 산에 더 가까운 부동산 사이에서 바뀔 수 있습니다. 평가는 일반적인 North Shore 사양에 의존하는 대신 현장별 노출 및 지붕 접근을 기록합니다.",
    assessmentFocus: [
      "가파르거나 높은 지붕 부분에 안전하게 접근",
      "지붕 수준 사이의 대용량 배수 경로",
      "그늘진 지붕 주변의 잔해 및 수분 보유",
    ],
    propertyContexts: ["가파른 곳", "분할 수준 주택", "숲이 우거진 주거용 부동산"],
    faqs: [
      {
        question: "차양 지붕에 다른 유지 관리 계획이 필요할 수 있나요?",
        answer:
          "그럴 수 있다. 건조 조건과 잔해 축적은 방향과 나무 덮개에 따라 다르므로 유지 관리 지침은 관찰된 지붕 영역을 기반으로 해야 합니다.",
      },
      {
        question: "가파른 부지가 프로젝트에 어떤 영향을 미치나요?",
        answer:
          "이는 액세스, 보호 및 작업 순서에 영향을 미칠 수 있습니다. 이러한 제약 조건은 작업 중에 예상치 못한 일로 처리되지 않고 범위가 확정되기 전에 문서화됩니다.",
      },
    ],
  },
  "west-vancouver": {
    intro:
      "웨스트 밴쿠버 거주지는 복잡한 매스 구조, 넓은 유리창, 테라스, 가파른 지붕과 낮은 경사의 지붕 형태를 혼합하여 사용하는 경우가 많습니다. 성공적인 지붕 작업은 건축물의 시야를 잃지 않으면서 전환을 해결하는 데 달려 있습니다.",
    localContext:
      "바다를 향한, 언덕 및 숲이 우거진 부동산은 동일한 자치단체의 지도에서 서로 다른 노출을 경험할 수 있습니다. Revolin은 시스템을 추천하기 전에 개별 지붕 형상, 접근 및 수로를 평가합니다.",
    assessmentFocus: [
      "건축용 금속, 멤브레인 및 경사진 지붕 영역 간의 연결",
      "난간, 지붕 데크, 벽 인터페이스 및 숨겨진 배수 장치",
      "완성된 외부 공간 주변의 접근 및 보호 계획",
    ],
    propertyContexts: ["건축가가 디자인한 주택", "힐사이드 레지던스", "혼합 재료 지붕 시스템"],
    faqs: [
      {
        question: "지붕 교체로 집의 건축적 특성이 보존될 수 있습니까?",
        answer:
          "그것은 권장 사항의 일부여야 합니다. 프로필, 색상, 가장자리 세부 정보 및 시각적 전환은 기술적 적합성과 함께 평가될 수 있습니다.",
      },
      {
        question: "혼합 지붕 시스템을 함께 평가하는 이유는 무엇입니까?",
        answer:
          "물은 지붕 영역 사이의 경계를 넘습니다. 한 표면에 맞는 세부 사항은 여전히 ​​다음 어셈블리에 안정적으로 연결되어야 합니다.",
      },
    ],
  },
  "new-westminster": {
    intro:
      "뉴웨스트민스터에는 오래된 주택, 개조된 주택, 여러 세대에 걸쳐 지붕 공사가 진행될 수 있는 소형 주거용 부지가 포함됩니다. 첫 번째 작업은 기존 어셈블리를 이해하고 나중에 변경 사항이 연결되는 위치를 이해하는 것입니다.",
    localContext:
      "나이만으로는 지붕 상태를 결정할 수 없습니다. Revolin은 덮개, 후레싱, 전환 및 배수 지점에서 증거를 찾은 다음 수리 가능한 세부 사항과 광범위한 시스템 문제를 구별합니다.",
    assessmentFocus: [
      "지붕 표면에 보이는 과거 수리 또는 개조 층",
      "추가, 현관 및 부착 구조물의 전환",
      "컴팩트한 부지의 자재 접근 및 이웃 보호",
    ],
    propertyContexts: ["오래된 단독 주택", "개조된 주택", "조밀한 주거용지"],
    faqs: [
      {
        question: "오래된 집에는 항상 전체 지붕을 다시 씌워야 합니까?",
        answer:
          "아니요. 집 자체의 연식보다 눈에 보이는 상태와 조립 성능이 더 중요합니다. 결정을 내리기 전에 문서화된 평가가 필요합니다.",
      },
      {
        question: "지붕에 이전 패치가 여러 개 있으면 어떻게 되나요?",
        answer:
          "이전 작업은 진단의 일부로 문서화됩니다. 한 영역에서 반복적으로 복구하면 해결되지 않은 전환이 발생할 수 있으며, 관련되지 않은 패치에는 다른 원인이 있을 수 있습니다.",
      },
    ],
  },
  "port-coquitlam": {
    intro:
      "포트 코퀴틀람 주택은 직선형 경사지붕과 차고, 증축물 또는 시간이 지남에 따라 추가된 지붕이 있는 야외 공간을 결합하는 경우가 많습니다. 이러한 보조 지붕 영역은 물이 계곡, 벽 및 홈통에 도달하는 방식을 변경할 수 있습니다.",
    localContext:
      "추가 부분 근처에 나타나는 누출이 반드시 바로 위에서 시작되는 것은 아닙니다. 평가는 수리 ​​영역을 정의하기 전에 지붕 면과 전환을 따릅니다.",
    assessmentFocus: [
      "주 지붕, 차고 및 이후 추가 건물 간의 연결",
      "상부 지붕면에서 물을 받는 하부 지붕 부분",
      "지붕과 벽 연결부의 관통 및 후레싱",
    ],
    propertyContexts: ["차고가 연결된 주택", "확장된 단독주택", "다단계 지붕 배수"],
    faqs: [
      {
        question: "누출이 발생원에서 멀리 떨어진 곳에서 나타날 수 있는 이유는 무엇입니까?",
        answer:
          "물은 눈에 보이기 전에 덮개, 골조 또는 지붕 전환을 따라 이동할 수 있습니다. 진단은 내부 얼룩을 원인으로 취급하는 대신 예상되는 경로를 따릅니다.",
      },
      {
        question: "차고와 현관 지붕도 평가에 포함됩니까?",
        answer:
          "특히 주 지붕에 연결되거나 주 지붕에서 배수를 받는 곳에 있을 수 있습니다. 요청된 평가 범위는 검토할 영역을 식별해야 합니다.",
      },
    ],
  },
  "port-moody": {
    intro:
      "Port Moody의 유입구를 향한 숲이 우거진 주거 환경은 습기, 잔해 및 변화하는 노출을 동일한 지붕의 다른 측면에 배치할 수 있습니다. 검사 노트는 지붕 면에 따라 이러한 조건을 구별해야 합니다.",
    localContext:
      "지붕 방향, 인근 초목 및 건물 높이가 재료 이름만큼 중요할 수 있습니다. Revolin은 유지 관리, 수리 또는 교체 옵션을 논의하기 전에 이러한 변수를 문서화합니다.",
    assessmentFocus: [
      "가장자리, 벽 및 관통부에서의 바람에 의한 비",
      "계곡 및 배수 지점의 잔해물 수집",
      "그늘진 지붕면과 노출된 지붕면의 다양한 풍화 패턴",
    ],
    propertyContexts: ["유입구를 향한 속성", "숲이 우거진 부지", "다중 평면 단독 주택"],
    faqs: [
      {
        question: "한 지붕의 서로 다른 측면이 다르게 노화될 수 있습니까?",
        answer:
          "예. 태양, 그늘, 바람 및 잔해 노출은 방향에 따라 다릅니다. 상태 보고서는 전체 지붕에 하나의 조건을 할당하기보다는 각 지붕 영역을 설명해야 합니다.",
      },
      {
        question: "표면 잔해가 지붕이 손상되었다는 증거입니까?",
        answer:
          "아니요. 이는 유지 관리 및 배수 관찰이지 대체 진단 자체가 아닙니다. 기본 덮개와 세부 사항은 여전히 ​​평가되어야 합니다.",
      },
    ],
  },
  "maple-ridge": {
    intro:
      "Maple Ridge에는 대규모 주거용 부동산, 숲이 우거진 동네, 노출과 접근이 상당히 다를 수 있는 산기슭 부지가 포함됩니다. 지붕과 주변 조건은 하나의 프로젝트 계획의 일부로 검토됩니다.",
    localContext:
      "더 높거나 더 많이 노출된 지역에서는 낮은 지역을 대표하지 않는 조건이 발생할 수 있습니다. 따라서 시스템 선택과 세부 사항은 지역적 가정이 아닌 유산과 연관되어 있습니다.",
    assessmentFocus: [
      "더 크거나 경사진 현장에 대한 접근, 준비 및 보호",
      "성숙한 식물 근처의 배수 및 잔해물 관리",
      "능선, 처마 및 지붕 가장자리의 노출",
    ],
    propertyContexts: ["더 큰 주거용 부지", "산기슭 지역", "숲이 우거진 속성"],
    faqs: [
      {
        question: "더 큰 부동산이 지붕 권장 사항을 변경합니까?",
        answer:
          "그 자체로는 아닙니다. 접근 및 프로젝트 물류를 변경할 수 있지만 지붕 시스템 권장 사항은 여전히 ​​경사, 조립, 노출 및 조건에 따라 달라집니다.",
      },
      {
        question: "더 많이 노출된 지붕 가장자리는 어떻게 검토됩니까?",
        answer:
          "평가에서는 가시적인 가장자리 상태, 고정 관련 문제 및 전환 세부 정보를 문서화한 다음 선택한 시스템 및 적용 가능한 요구 사항에 대한 권장 사항을 연결합니다.",
      },
    ],
  },
  delta: {
    intro:
      "Delta의 지역사회에는 해안, 농업 지역 및 내륙 주거 환경이 포함됩니다. 이러한 다양한 노출로 인해 솔루션을 선택하기 전에 개별 현장의 바람, 배수 및 지붕 형상을 평가하는 것이 중요합니다.",
    localContext:
      "Tsawwassen, Ladner 또는 North Delta 부동산은 지방자치단체 이름만을 기준으로 추천을 받아서는 안 됩니다. Revolin은 주소에서 관찰된 건물과 조건을 기준으로 범위를 결정합니다.",
    assessmentFocus: [
      "처마, 능선 및 주변 세부사항의 바람 노출",
      "넓은 지붕면의 배수 용량 및 배출",
      "다르게 노출된 입면의 재료 상태",
    ],
    propertyContexts: ["해안 지역", "개방형 주거지", "내륙 단독주택"],
    faqs: [
      {
        question: "Delta 전체에 동일한 지붕 시스템이 적합한가요?",
        answer:
          "자동으로 도시 전체를 선택할 수는 없습니다. 경사, 노출, 건축, 배수 및 예산은 모두 적절한 시스템에 영향을 미칩니다.",
      },
      {
        question: "강풍 발생 후 점검 사항은 무엇입니까?",
        answer:
          "대상 평가를 통해 접근 가능한 지붕 가장자리, 덮개, 후레싱 및 관통부에 눈에 띄는 변화가 있는지 검토할 수 있습니다. 안전이 최우선입니다. 주택 소유자는 젖거나 손상된 지붕 위로 올라가서는 안 됩니다.",
      },
    ],
  },
  langley: {
    intro:
      "Langley 부동산에는 더 큰 단독 주택, 넓은 지붕 범위 및 여러 개의 부착 구조물이 포함될 수 있습니다. 명확한 범위는 각 지붕 영역을 분리하는 동시에 배수 및 연결 방법을 해결합니다.",
    localContext:
      "개방된 부지와 나무가 우거진 지역은 유지 관리 및 노출 고려 사항이 다릅니다. Revolin은 자재나 수리 한계를 권장하기 전에 현장의 실제 조건을 기록합니다.",
    assessmentFocus: [
      "넓은 지붕면과 긴 배수로",
      "집, 차고, 지붕이 있는 야외 공간 사이의 연결",
      "개방된 노출 구역과 나무로 보호된 구역의 차이점",
    ],
    propertyContexts: ["더 큰 단독 주택", "부착된 구조물이 있는 주택", "개방형 주거지"],
    faqs: [
      {
        question: "프로젝트를 지붕 면적에 따라 단계적으로 진행할 수 있나요?",
        answer:
          "때로는 어셈블리와 전환이 내구성 있는 중지 지점을 허용하는 경우에만 가능합니다. 범위는 단계적 작업이 연결된 영역에 어떻게 영향을 미치는지 설명해야 합니다.",
      },
      {
        question: "넓은 지붕 면에는 하나의 특정 재료가 필요합니까?",
        answer:
          "아니요. 형상은 하나의 입력입니다. 경사도, 조립설계, 노출, 외관, 유지관리 우선순위, 예산 등을 함께 고려해야 합니다.",
      },
    ],
  },
  "white-rock": {
    intro:
      "White Rock 주택은 바다를 향한 노출과 현대적인 지붕 형태, 발코니 및 낮은 경사 구역을 결합할 수 있습니다. 가장자리, 벽 전환 및 배수 세부 사항은 해당 아키텍처의 맥락에서 검토됩니다.",
    localContext:
      "해안 조건으로 인해 하나의 지붕 제품이 보편적으로 정확하지는 않습니다. 경사면 및 주택 소유자 우선순위와 함께 제품 적합성, 시스템 설계 및 눈에 보이는 세부 사항도 고려됩니다.",
    assessmentFocus: [
      "집중된 비에 노출된 주변 및 전환 세부정보",
      "난간, 연석 및 인접한 벽 주변의 낮은 경사 배수",
      "선택한 어셈블리와 눈에 보이는 금속 디테일의 호환성",
    ],
    propertyContexts: ["바다를 향한 주택", "현대 레지던스", "혼합 경사 지붕 설계"],
    faqs: [
      {
        question: "바다에 노출되면 지붕 재료가 자동으로 제외됩니까?",
        answer:
          "자동으로 아닙니다. 전체 시스템, 제품 제한 사항, 코팅 또는 마감, 고정 및 전환 세부 사항을 모두 해당 속성에 대해 검토해야 합니다.",
      },
      {
        question: "발코니와 난간 연결이 중요한 이유는 무엇입니까?",
        answer:
          "이는 단순한 물 흐름을 방해하고 벽, 연석 또는 막 전환을 도입할 수 있습니다. 이러한 인터페이스는 문서화된 평가에 포함되어야 합니다.",
      },
    ],
  },
} as const satisfies Record<ServiceAreaSlug, ServiceAreaContent>;

export const ui = {
  common: {
    home: "집",
    services: "서비스",
    serviceAreas: "서비스 지역",
    contact: "문의",
    faq: "FAQ",
    learnMore: "자세히 알아보기",
    requestAssessment: "지붕 상태 점검 요청",
    exploreServices: "지붕 서비스 살펴보기",
    viewContactOptions: "모든 연락처 옵션 보기",
  },
  serviceDetail: {
    breadcrumbLabel: "이동 경로",
    call: "전화",
    whoForEyebrow: "이 서비스가 필요한 경우",
    whoForTitle: "제품보다 지붕 상태를 먼저 확인합니다",
    warningSignsTitle: "일반적인 경고 신호",
    assessmentEyebrow: "평가 규율",
    evaluatesTitle: "Revolin이 평가하는 것",
    solutionsEyebrow: "솔루션 카테고리",
    solutionsTitle: "해결책은 정해진 판매 문구가 아니라 지붕 상태에 따라 결정됩니다",
    repairEyebrow: "수리가 합리적일 수 있는 경우",
    replaceEyebrow: "교체를 의미할 수 있는 경우",
    scopeEyebrow: "범위",
    scopeTitle: "업무에 영향을 미치는 것",
    scopeBody:
      "실제 작업 범위는 평가 후 정해집니다. 가격, 수명 및 지붕 시스템은 건물과 선택한 사양에 따라 달라지므로 Revolin Roofing은 획일적인 기준을 제시하지 않습니다.",
    questionsEyebrow: "질문",
    questionsTitle: "결정하기 전에",
    nextStepEyebrow: "다음 단계",
    nextStepTitle: "지붕에 대한 자세한 평가를 요청하세요",
    nextStepBody:
      "확인하신 문제와 주택 위치를 알려 주세요. 이 정보를 바탕으로 적절한 다음 상담을 준비합니다.",
  },
  servicesIndex: {
    eyebrow: "지붕 서비스",
    title: "올바른 범위는 올바른 진단에서 시작됩니다",
    body: "Revolin Roofing의 주요 서비스 카테고리를 살펴보세요. 각각은 미리 결정된 제품 추천이 아닌 건물, 지붕 시스템 및 우리가 볼 수 있는 조건으로 시작됩니다.",
    exploreService: "서비스 살펴보기",
    technicalEyebrow: "기술 참조",
    technicalTitle: "현재 BC 자원이 중요합니다",
    rcabcTitle: "RCABC 지붕 작업 매뉴얼",
    rcabcBody:
      "현재 현행 지붕 시공 기준 자료; 링크가 Revolin 멤버십 또는 RoofStar를 보장한다는 의미는 아닙니다.",
    bcCodesTitle: "BC 코드",
    bcCodesBody: "관할권 및 프로젝트 범위에 대한 주정부 규정, 개정 및 공지.",
    vancouverBylawTitle: "밴쿠버 건물 조례",
    vancouverBylawBody: "밴쿠버 특정 요구 사항은 주정부 규정과 별도로 검토됩니다.",
  },
  areaIndex: {
    breadcrumbLabel: "이동 경로",
    eyebrow: "메트로 밴쿠버와 로어 메인랜드",
    title: "지붕 서비스 지역",
    body: "Revolin Roofing은 다음 지역 사회에서 주거용 지붕 평가를 제공합니다. 각 프로젝트는 건물의 지붕 형태, 노출, 접근 및 해당 지역 요구 사항에 맞게 계획됩니다.",
    coverageEyebrow: "지점이 아닌 보장 범위",
    coverageTitle: "실제 지점으로 오해시키지 않는 지역 정보",
    coverageBody:
      "이 페이지에서는 지붕 평가가 가능한 곳을 설명합니다. 그들은 Revolin Roofing이 나열된 모든 도시에 사무실을 유지한다고 주장하지 않습니다. 프로젝트 적용 범위를 확인하려면 부동산 주소로 저희에게 연락하십시오.",
    nearbyEyebrow: "근처 커뮤니티?",
    nearbyTitle: "프로젝트 범위를 확인하도록 요청하세요.",
    contactCta: "리볼린 루핑에 문의하세요",
  },
  serviceArea: {
    breadcrumbLabel: "이동 경로",
    metroVancouver: "메트로 밴쿠버",
    lowerMainland: "로워 메인랜드",
    roofingServicesSuffix: "지붕 서비스",
    titlePrefix: "지붕 서비스",
    confirmedCommunity:
      "{area}은(는) 확인된 서비스 커뮤니티입니다. 해당 지역 지점 청구가 아닌 서비스 지역 페이지입니다.",
    localContextEyebrow: "지역별 지붕 환경",
    localContextTitle: "권장 사항은 주택의 실제 조건에 따라 달라집니다",
    propertyContextsLabel: "계획할 때 고려하는 주택 조건",
    prioritiesEyebrow: "평가 우선순위",
    prioritiesTitle: "{area}에서 주목할 만한 것은 무엇입니까?",
    planningDisclaimer:
      "이는 원격 진단이나 엔지니어링 의견이 아닌 일반적인 계획 정보입니다. 특정 부동산에 대한 조사 결과 및 적용 가능한 요구 사항이 확인되었습니다.",
    relatedEyebrow: "관련 서비스",
    relatedTitle: "{area} 주택을 위한 지붕 공사 결정",
    relatedBody:
      "관찰된 문제와 지붕 상태부터 확인합니다. 다음 단계는 부분 수리, 예방 점검 또는 계획된 교체일 수 있습니다.",
    fallbackServiceName: "지붕 서비스",
    faqSuffix: "FAQ",
    faqTitle: "평가 전 질문",
    requestEyebrow: "평가 요청",
    requestTitle: "우리 앞에 있는 지붕부터 시작해 보겠습니다.",
    requestBody:
      "{area}의 주택, 지붕 구역과 확인하신 문제를 알려 주세요. 이 정보는 평가 준비에 도움이 되지만 현장 점검을 대신하지는 않습니다.",
  },
  about: {
    eyebrow: "레볼린 그룹 회사",
    title: "건설 회사의 관점으로 접근하는 지붕 전문성",
    body: "Revolin Group의 전문 지붕 부문인 Revolin Roofing은 원래 브랜드의 건축적 정교함, 투명한 프로세스 및 규율 있는 프로젝트 사고 방식을 지붕 시스템에 도입합니다.",
    relationshipEyebrow: "관계",
    relationshipTitle: "지붕공사 전문. 건물 전체와 연결되어 있습니다.",
    relationshipBody1:
      "지붕은 고립된 표면이 아닙니다. 이는 벽, 클래딩, 채광창, 환기, 배수 및 내부 조립을 다룹니다. Revolin Roofing은 더 넓은 건축 맥락을 고려하여 이러한 인터페이스에 접근합니다.",
    relationshipBody2:
      "이 접근 방식은 명확한 진단과 범위를 지원합니다. 이는 지붕 작업 외부에서 상황이 발생할 때 적합한 엔지니어, 설계자 또는 전문가의 필요성을 대체하지 않습니다. 이러한 제한 사항을 조기에 식별하는 데 도움이 됩니다.",
    principlesEyebrow: "업무 원칙",
    principlesTitle: "모든 결정 지점의 명확성",
    assessTitle: "추천하기 전에 평가하세요",
    assessBody:
      "범위를 제안하기 전에 눈에 보이는 증상, 가능한 원인 및 일반적인 조립 조건을 고려합니다.",
    architectureTitle: "건축을 존중하라",
    architectureBody:
      "재료, 색상, 프로필, 가장자리 및 전환은 물이 흐르는 방식뿐만 아니라 건물이 읽는 방식에도 영향을 미칩니다.",
    limitsTitle: "경계를 명시하세요",
    limitsBody: "추가 조사가 필요한 가정, 제외 및 조건은 협상되고 범위가 지정됩니다.",
    parentEyebrow: "모회사",
    parentTitle: "Revolin 그룹 살펴보기",
    parentBody: "개조, 건축 및 일반 건축 작업에 대해서는 모회사 사이트를 방문하십시오.",
    parentCta: "RevolinGroup.com 방문",
  },
  beforeAfter: {
    eyebrow: "시공 전후",
    title: "메트로 밴쿠버 전역의 시공 전후 지붕 갤러리",
    body: "메트로 밴쿠버와 로어메인랜드 주택 소유주를 위한 Revolin Roofing 시공 전후 갤러리를 둘러보세요. 긴급 누수 보수부터 아스팔트·금속·시더·평지붕 교체까지, 각 갤러리는 문제, 진단, 완료된 지붕의 맥락을 보여 줍니다.",
    notice:
      "서비스, 지붕 유형 또는 도시로 필터링해 자택과 가장 가까운 사례를 찾은 뒤, 해당 건물에 맞는 안내를 위해 지붕 평가를 요청하세요.",
    projectHeroBody:
      "메트로 밴쿠버 전역의 Revolin Roofing 시공 전후 변화를 살펴보세요. 긴급 누수 보수부터 아스팔트·금속·시더·평지붕 전체 교체까지, 각 갤러리는 로어메인랜드 주택 소유주가 마주하는 문제, 진단, 완료된 지붕 결과를 강조합니다.",
  },
  faqPage: {
    eyebrow: "주택 소유주 안내",
    title: "지붕 관련 질문, 판매 스크립트 없이 답변됨",
    body: "평가, 수리, 교체 및 지붕 시스템에 대한 실제 정보. 프로젝트별 상담은 현장 검토 후 진행됩니다.",
  },
  contactPage: {
    eyebrow: "리볼린 루핑에 문의하세요",
    title: "당신이 본 것을 우리에게 말해주세요",
    body: "부동산의 위치, 지붕 유형(알고 있는 경우), 고려 중인 우려 사항이나 프로젝트를 공유하세요. 양식은 구성된 리드 서비스가 이를 수락한 경우에만 전달을 확인합니다.",
    directEyebrow: "직접 문의",
    directTitle: "기본부터 시작하세요",
    phone: "전화",
    email: "이메일",
    serviceRegion: "서비스 지역",
    regionValue: "메트로 밴쿠버와 로어 메인랜드",
  },
  legal: {
    legal: "법률",
    privacyPolicy: "개인 정보 보호 정책",
    websiteTerms: "웹사이트 이용약관",
    privacyBody: "웹사이트 쿼리, 속성 데이터 및 필수 브라우저 저장 공간을 처리하는 방법입니다.",
    termsBody: "Revolin Roofing 웹사이트 및 일반 교육 자료를 사용할 때 적용되는 규칙입니다.",
    lastUpdated: "최종 업데이트 날짜: 2026년 8월 10일.",
  },
} as const;

export type ContentUi = typeof ui;

// Semantic aliases used by the locale-content registry.
export const serviceContent = services;
export const serviceAreaContent = serviceAreas;

const content = { services, serviceAreas, ui } as const;
export default content;
